/**
 * Sites Management Page
 * Manages sites (containers for assets/POIs)
 * Clean implementation using simplified services
 */

import React, { useState, useEffect, useRef } from 'react';
import { Asset, CreateAssetRequest, AssetFile } from '../services/AssetService';
import { assetService } from '../services/AssetService';
import { fileService } from '../services/FileService';
import { apiService } from '../services/ApiService';
import { parseCustomData } from '../utils/POIFieldExtractor';
import { useViewerPOIBatchActions } from './shared/useViewerPOIBatchActions';
// P4 payload fix (2026-08): SitesPage is in the eager boot bundle — value-
// importing anything that reaches web-ifc drags ~716KB of IFC engine into
// every page load. Types are erased; the converter is dynamic-imported at its
// single call site.
import type { IFCElementInfo } from '../services/IFCAnalysisService';
import { accService, ACCConnection, ACCHub, ACCProject, ACCFolderItem, ACCLink, ACCLevel, ACCFileVersion } from '../services/ACCService';
import { useACCConnection } from '../pages/sites/useACCConnection';
import { useFileUpload } from '../pages/sites/useFileUpload';
import ACCModals from '../pages/sites/ACCModals';
import SiteModals from '../pages/sites/SiteModals';
import UploadModals from '../pages/sites/UploadModals';
import ListModals from '../pages/sites/ListModals';
import SiteTiles, { isReadOnlySitesVisitor } from '../pages/sites/SiteTiles';
import ConfigModals from '../pages/sites/ConfigModals';
import BatchModals from '../pages/sites/BatchModals';
import onboardingService from '../services/OnboardingService';
import { coordinateTransformService } from '../services/CoordinateTransformService';
import ProjectOnboardingPage from './ProjectOnboardingPage';
import { useAuth } from '../contexts/AuthContext';
import { useRBAC } from '../contexts/RBACContext';
import { OnboardingSession } from '../types/Onboarding';
// theme.css now loads at the app entry (src/index.tsx) — see E-7.
import { showErrorToast, showSuccessToast } from './shared/toast';
import { logger } from '../utils/log';

interface AutodeskViewerConfig {
  urn: string;
  connectionId: string;
  assetId?: string;
  viewables: Array<{ guid: string; name: string; role: '3d' | '2d' }>;
  fileName?: string;
  defaultLevelName?: string | null;
  defaultLevelElevation?: number | null;
  initialViewableGuid?: string | null;
}

interface SitesPageProps {
  onAssetSelected?: (asset: Asset) => void;
  onAssetUpdated?: (assetId: string) => void;
  onOpenAutodeskViewer?: (config: AutodeskViewerConfig) => void;
  onAccLinksChanged?: () => void;
  onOpenSiteViewer?: (siteId: string) => void;
  translate?: (key: string) => string;
  currentLanguage?: 'en' | 'he';
}

interface E57CoordinateReferenceInfo {
  status?: string;
  source?: string;
  label?: string;
  authority?: string | null;
  code?: string | number | null;
  rawCoordinateMetadataPresent?: boolean;
}

interface E57CoordinateReferenceDisplay {
  label: string;
  detail?: string;
}

interface E57SphereNavigationSettings {
  placementMode: 'legacy' | 'true_vector';
  scaleToMeters: number;
  verticalScaleToMeters: number;
  cameraHeightAboveFloorMeters: number;
  maxDistanceMeters: number;
  markerNearSize: number;
  markerFarSize: number;
}

const DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS: E57SphereNavigationSettings = {
  placementMode: 'legacy',
  scaleToMeters: 1,
  verticalScaleToMeters: 1,
  cameraHeightAboveFloorMeters: 1.7,
  maxDistanceMeters: 10,
  markerNearSize: 1.28,
  markerFarSize: 0.58
};

const SitesPage: React.FC<SitesPageProps> = ({
  onAssetSelected,
  onAssetUpdated,
  onOpenAutodeskViewer,
  onAccLinksChanged,
  onOpenSiteViewer,
  translate,
  currentLanguage
}) => {
  // Get current user for role-based access control
  const { user, refreshToken } = useAuth();
  const { isAdmin, hasRole, canCreateSites, refreshRoles } = useRBAC();

  // RBAC-based asset management permission (admin, data_creator, or write permission)
  // Note: account_admin and site_admin can only manage USERS/permissions, not site content
  const canManageAsset = (asset: Asset | null): boolean => {
    if (!asset) return false;
    if (isAdmin) return true;
    // "Upload files" switched off on this person's assignment (Idan 2026-10-11): Sites is the
    // simple tiles page for them, with nothing to upload — they still create assets and edit
    // rooms and tickets in the viewer and on those pages.
    if (asset.can_upload_files === false) return false;
    if (hasRole('data_creator', asset.id)) return true;
    return asset.permission_level === 'write';
  };

  const getLocalRvtFiles = (asset: Asset | null): AssetFile[] => {
    const files = asset?.files as any;
    if (!files) return [];
    if (Array.isArray(files.rvtFiles)) return files.rvtFiles;
    return files.rvt ? [files.rvt] : [];
  };

  // Translation helper — same signature as the canonical TranslationContext t()
  // (F-3: seven call sites here were already passing fallback strings that the old
  // 1-arg wrapper silently discarded; missing keys rendered as raw key names).
  const t = (key: string, fallback?: string) => {
    const translated = translate?.(key);
    return (translated && translated !== key ? translated : undefined) || fallback || key;
  };

  // ═══════════════════════════════════════════════════════════════
  // Shared file card helpers — used by IFC, ACC RVT, and Local RVT cards
  // ═══════════════════════════════════════════════════════════════

  const fileIconSvg = (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
      <path d="M12 18v-6"/>
      <path d="M9 15l3 3 3-3"/>
    </svg>
  );

  const iconSvgs: Record<string, React.ReactNode> = {
    delete: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="3 6 5 6 21 6"/>
        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
      </svg>
    ),
    play: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="5 3 19 12 5 21 5 3"/>
      </svg>
    ),
    sync: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10"/>
        <polyline points="1 20 1 14 7 14"/>
        <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
      </svg>
    ),
    settings: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3"/>
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
      </svg>
    ),
    star: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
      </svg>
    ),
    edit: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M12 20h9"/>
        <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>
      </svg>
    ),
    x: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <line x1="18" y1="6" x2="6" y2="18"/>
        <line x1="6" y1="6" x2="18" y2="18"/>
      </svg>
    ),
    pause: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="6" y="4" width="4" height="16"/>
        <rect x="14" y="4" width="4" height="16"/>
      </svg>
    ),
    restart: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <polyline points="23 4 23 10 17 10"/>
        <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
      </svg>
    ),
  };

  const renderProgressBar = (progress: number, stuckMessage?: string) => (
    <div className="ifc-conversion-status">
      <div className="ifc-progress-bar">
        <div
          className={`ifc-progress-fill${stuckMessage ? ' ifc-progress-stuck' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>
      {stuckMessage && (
        <div style={{ fontSize: '11px', color: '#f59e0b', marginTop: '4px' }}>
          {stuckMessage}
        </div>
      )}
    </div>
  );

  const renderMetaItem = (label: string, value: React.ReactNode) => (
    <div className="ifc-meta-item">
      <span className="ifc-meta-label">{label}</span>
      <span className="ifc-meta-value">{value}</span>
    </div>
  );

  const renderMetaSelect = (
    label: string,
    value: string,
    options: { key: string; label: string }[],
    onChange: (val: string) => void,
    title?: string,
    defaultLabel?: string
  ) => (
    <div className="ifc-meta-item">
      <span className="ifc-meta-label">{label}</span>
      <select
        className="ifc-level-select"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        title={title}
      >
        <option value="">{defaultLabel || 'Auto (default)'}</option>
        {options.map(opt => (
          <option key={opt.key} value={opt.key}>{opt.label}</option>
        ))}
      </select>
    </div>
  );

  const formatLevelOptionLabel = (level: ACCLevel | any) => {
    const elevation = Number(level?.elevation);
    const elevationLabel = Number.isFinite(elevation) ? elevation.toFixed(1) : '?';
    return `${level?.name || 'Level'} (${elevationLabel}m)`;
  };

  const renderSpawnLevelControl = (
    value: string,
    levels: Array<ACCLevel | any>,
    onChange: (val: string) => void,
    refreshKey?: string,
    onRefresh?: () => void,
    canRefresh: boolean = false
  ) => {
    const hasLevels = levels.length > 0;
    const isRefreshing = refreshKey ? refreshingRvtLevels[refreshKey] === true : false;

    return (
      <div className="ifc-meta-item">
        <span className="ifc-meta-label">Spawn Level</span>
        <div className="ifc-level-control">
          <select
            className="ifc-level-select"
            value={hasLevels ? value : ''}
            onChange={(e) => onChange(e.target.value)}
            disabled={!hasLevels}
            title={hasLevels ? 'Select default spawn level' : 'No levels found in Autodesk metadata'}
          >
            <option value="">{hasLevels ? 'Auto (ground)' : 'No levels found'}</option>
            {levels.map((level) => (
              <option key={`${level.name}:${level.elevation}`} value={level.name}>{formatLevelOptionLabel(level)}</option>
            ))}
          </select>
          {!hasLevels && canRefresh && onRefresh && (
            <button
              type="button"
              className="ifc-level-refresh-btn"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onRefresh();
              }}
              disabled={isRefreshing}
              title={isRefreshing ? 'Refreshing levels' : 'Refresh levels'}
            >
              {iconSvgs.sync}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderActionBtn = (
    icon: string,
    label: string,
    onClick: (e?: React.MouseEvent) => void,
    opts?: { disabled?: boolean; style?: React.CSSProperties; className?: string; title?: string }
  ) => (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClick(e); }}
      className={`dt-btn sm${opts?.className ? ` ${opts.className}` : ''}`}
      disabled={opts?.disabled}
      title={opts?.title}
      style={{ display: 'inline-flex', alignItems: 'center', gap: label ? '5px' : '0', ...opts?.style }}
    >
      {iconSvgs[icon]}
      {label}
    </button>
  );

  // ═══════════════════════════════════════════════════════════════

  const onboardingStatusBadgeStyles: Record<string, { background: string; color: string }> = {
    draft: { background: '#fef3c7', color: '#92400e' },
    processing: { background: '#dbeafe', color: '#1d4ed8' },
    ready: { background: '#dcfce7', color: '#166534' },
    live: { background: '#d1fae5', color: '#065f46' },
    failed: { background: '#fee2e2', color: '#991b1b' }
  };

  const renderOnboardingBadge = (status: OnboardingSession['status']) => {
    const scheme = onboardingStatusBadgeStyles[status] || { background: 'var(--color-surface)', color: 'var(--color-text-primary)' };
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '4px 8px',
        borderRadius: '999px',
        fontSize: '11px',
        fontWeight: 600,
        background: scheme.background,
        color: scheme.color
      }}>
        {status}
      </span>
    );
  };

  // {}-fallback wrapper over the canonical parser (utils/POIFieldExtractor) — this page's
  // three inline re-parses of selectedAsset.custom_data were converged onto it (2026-07-06).
  const parseAssetCustomData = (asset: Asset | null): Record<string, any> => {
    return parseCustomData(asset) || {};
  };

  const getPanoramaToRVTTransform = (asset: Asset | null) => {
    const customData = parseAssetCustomData(asset);
    const configuredTransform = customData.coordinate_transforms?.panorama_to_rvt_model || customData.panorama_to_rvt_model || {};
    const hasLegacyFlatFields = [
      'panorama_to_rvt_offset_x',
      'panorama_to_rvt_offset_y',
      'panorama_to_rvt_offset_z',
      'panorama_to_rvt_rotation_deg',
      'panorama_to_rvt_scale',
      'panorama_to_rvt_swap_xy',
      'panorama_to_rvt_flip_x',
      'panorama_to_rvt_flip_y',
      'panorama_to_rvt_flip_z'
    ].some((key) => customData[key] !== undefined);

    return {
      enabled: Boolean(customData.coordinate_transforms?.panorama_to_rvt_model || customData.panorama_to_rvt_model || hasLegacyFlatFields),
      sourceFrame: configuredTransform.sourceFrame || 'panorama_raw',
      targetFrame: configuredTransform.targetFrame || 'rvt_model',
      translation: {
        x: Number(configuredTransform.translation?.x ?? customData.panorama_to_rvt_offset_x ?? 0),
        y: Number(configuredTransform.translation?.y ?? customData.panorama_to_rvt_offset_y ?? 0),
        z: Number(configuredTransform.translation?.z ?? customData.panorama_to_rvt_offset_z ?? 0)
      },
      rotationDeg: Number(configuredTransform.rotationDeg ?? customData.panorama_to_rvt_rotation_deg ?? 0),
      scale: {
        x: Number(configuredTransform.scale?.x ?? configuredTransform.scale ?? customData.panorama_to_rvt_scale ?? 1),
        y: Number(configuredTransform.scale?.y ?? configuredTransform.scale ?? customData.panorama_to_rvt_scale ?? 1),
        z: Number(configuredTransform.scale?.z ?? configuredTransform.scale ?? customData.panorama_to_rvt_scale ?? 1)
      },
      swapXY: configuredTransform.swapXY ?? customData.panorama_to_rvt_swap_xy ?? false,
      flipX: configuredTransform.flipX ?? customData.panorama_to_rvt_flip_x ?? false,
      flipY: configuredTransform.flipY ?? customData.panorama_to_rvt_flip_y ?? false,
      flipZ: configuredTransform.flipZ ?? customData.panorama_to_rvt_flip_z ?? false
    };
  };

  /**
   * THE writer for everything stored on asset.custom_data (panorama↔RVT transform, E57 source
   * settings). updateAsset returns null instead of throwing (AssetService.ts:96), so a rejected
   * save used to fall straight through to loadAssets() and the panel simply re-rendered the old
   * numbers as if nothing had happened. Returns whether the write landed so callers can stop.
   */
  const updateSelectedAssetCustomData = async (updater: (currentCustomData: Record<string, any>) => Record<string, any>): Promise<boolean> => {
    if (!selectedAsset) return false;

    const currentCustomData = parseAssetCustomData(selectedAsset);
    const updatedCustomData = updater(currentCustomData);

    const payload = {
      ...selectedAsset,
      custom_data: updatedCustomData
    };
    const saved = await assetService.updateAsset(selectedAsset.id, payload);
    if (!saved) {
      showErrorToast('Failed to save the setting. Please try again.');
      return false;
    }
    await loadAssets();
    return true;
  };

  /**
   * The eight GEOTIFF alignment fields each wrote one custom_data key on blur and checked nothing.
   * Their number inputs are uncontrolled, so a refused save left the typed value sitting on screen
   * looking committed — the classic "it saved until I reloaded" report. One helper so the eight
   * stay in step: report the failure and put the field back to the value the server still holds.
   */
  const saveGeotiffSetting = async (
    field: string,
    value: number | boolean,
    label: string,
    input?: HTMLInputElement
  ): Promise<void> => {
    if (!selectedAsset) return;

    const payload = {
      ...selectedAsset,
      custom_data: {
        ...(selectedAsset.custom_data || {}),
        [field]: value
      }
    };
    const saved = await assetService.updateAsset(selectedAsset.id, payload);
    if (!saved) {
      // defaultValue is what the last render put there from the asset, i.e. the stored value.
      // (The checkboxes are controlled, so React rolls those back on its own.)
      if (input) input.value = input.defaultValue;
      showErrorToast(`Failed to save ${label}. Please try again.`);
      return;
    }
    await loadAssets();
  };

  const updatePanoramaToRVTTransform = async (updater: (currentTransform: any) => any): Promise<boolean> => {
    return updateSelectedAssetCustomData((currentCustomData) => {
      const existingTransform = currentCustomData.coordinate_transforms?.panorama_to_rvt_model || currentCustomData.panorama_to_rvt_model || {
        sourceFrame: 'panorama_raw',
        targetFrame: 'rvt_model',
        translation: { x: 0, y: 0, z: 0 },
        rotationDeg: 0,
        scale: { x: 1, y: 1, z: 1 },
        swapXY: false,
        flipX: false,
        flipY: false,
        flipZ: false
      };

      const nextTransform = updater(existingTransform);

      return {
        ...currentCustomData,
        coordinate_transforms: {
          ...(currentCustomData.coordinate_transforms || {}),
          panorama_to_rvt_model: {
            sourceFrame: 'panorama_raw',
            targetFrame: 'rvt_model',
            ...nextTransform,
            source: 'legacy_override',
            updatedAt: new Date().toISOString()
          }
        }
      };
    });
  };

  const resetPanoramaToRVTTransform = async () => {
    await updateSelectedAssetCustomData((currentCustomData) => {
      const nextCoordinateTransforms = { ...(currentCustomData.coordinate_transforms || {}) };
      delete nextCoordinateTransforms.panorama_to_rvt_model;

      const {
        panorama_to_rvt_model,
        panorama_to_rvt_offset_x,
        panorama_to_rvt_offset_y,
        panorama_to_rvt_offset_z,
        panorama_to_rvt_rotation_deg,
        panorama_to_rvt_scale,
        panorama_to_rvt_swap_xy,
        panorama_to_rvt_flip_x,
        panorama_to_rvt_flip_y,
        panorama_to_rvt_flip_z,
        ...restCustomData
      } = currentCustomData;

      return {
        ...restCustomData,
        coordinate_transforms: nextCoordinateTransforms
      };
    });
  };

  // State
  const [assets, setAssets] = useState<Asset[]>([]);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showGuidedCreateModal, setShowGuidedCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [matchingAssetIds, setMatchingAssetIds] = useState<string[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [sitesPanelTab, setSitesPanelTab] = useState<'files' | 'onboarding'>('files');
  const [onboardingSessions, setOnboardingSessions] = useState<OnboardingSession[]>([]);
  /**
   * Who sees the onboarding surfaces here. project_onboarding_manager was retired by
   * migration 069 (it read every site and could write in none), so the question is no longer
   * "what role do you hold" but "do you have onboarding work": admins always, and anyone the
   * server hands a session to. loadOnboardingSessions already asks exactly that — GET
   * /api/onboarding/projects is scoped to admins, session creators and assignees — so the
   * answer is simply whether it returned anything.
   */
  const isOnboardingManager = isAdmin || onboardingSessions.length > 0;
  const [loadingOnboardingSessions, setLoadingOnboardingSessions] = useState(false);
  const [creatingGuidedSite, setCreatingGuidedSite] = useState(false);

  // Form state
  const [newAsset, setNewAsset] = useState<CreateAssetRequest>({ name: '', description: '' });
  const [editAsset, setEditAsset] = useState<CreateAssetRequest>({ name: '', description: '' });
  const [showEditModal, setShowEditModal] = useState(false);
  // Per-asset, per-file-type file selections
  const [selectedFiles, setSelectedFiles] = useState<{[assetId: string]: {[fileType: string]: File}}>({});
  const [selectedCompanionFiles, setSelectedCompanionFiles] = useState<{[assetId: string]: File[]}>({});

  // Missing state variables for upload modals
  const [uploadFileType, setUploadFileType] = useState('ifc');

  // Individual upload modal states
  const [showIFCUploadModal, setShowIFCUploadModal] = useState(false);
  const [showE57UploadModal, setShowE57UploadModal] = useState(false);
  // const [showDWGUploadModal, setShowDWGUploadModal] = useState(false); // Commented out - using GEOTIFF instead
  const [showGEOTIFFUploadModal, setShowGEOTIFFUploadModal] = useState(false);
  const [showPOIUploadModal, setShowPOIUploadModal] = useState(false);

  // Panorama list modal state
  const [showPanoramaListModal, setShowPanoramaListModal] = useState(false);
  const [panoramas, setPanoramas] = useState([]);
  const [loadingPanoramas, setLoadingPanoramas] = useState(false);

  // POI list modal state
  const [showPOIListModal, setShowPOIListModal] = useState(false);
  const [pois, setPois] = useState([]);
  const [loadingPOIs, setLoadingPOIs] = useState(false);
  const [deletingPOI, setDeletingPOI] = useState<string | null>(null);
  const [deletingAllPOIs, setDeletingAllPOIs] = useState(false);
  const [selectedPOIs, setSelectedPOIs] = useState<Set<string>>(new Set());

  // Edit mode state for table
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedPOIs, setEditedPOIs] = useState<Record<string, any>>({});
  const [modifiedPOIs, setModifiedPOIs] = useState<Set<number>>(new Set()); // Track actually modified POIs
  const [savingPOIs, setSavingPOIs] = useState(false);

  // Set Systems / Set Locations batch actions — the shared hook (D-9 adopts the 3.2
  // follow-up; batch-modal copy 3). Transport adapter preserves this page's exact drift:
  // raw fetch + getAuthHeaders on selectedAsset?.id; open-load error → RESET to empties;
  // apply non-ok → throw → result{failed: selected count}; reload awaited via loadPOIs.
  // The page's CSS-class modal chrome stays its own (documented DOM NO-GO) in
  // pages/sites/BatchModals.tsx, reading the hook's state atoms below.
  const poiBatch = useViewerPOIBatchActions({
    getAssetId: () => selectedAsset?.id,
    selectedPOIs,
    reloadPOIs: (id) => loadPOIs(id),
    awaitReload: true,
    minSubcategoryLevels: 0,
    transport: {
      loadSystemsProperties: async (assetIdForLoad) => {
        try {
          const data = await apiService.get(`/assets/${assetIdForLoad}/pois/available-properties`);
          return { properties: data.properties || [], propertyValues: data.propertyValues || {} };
        } catch (error) {
          console.error('Failed to load available properties:', error);
          return { properties: [], propertyValues: {} };
        }
      },
      loadLocationsProperties: async (assetIdForLoad) => {
        try {
          const data = await apiService.get(`/assets/${assetIdForLoad}/pois/available-properties`);
          return { properties: data.properties || [] };
        } catch (error) {
          console.error('Failed to load available properties:', error);
          return { properties: [] };
        }
      },
      applySystems: async (assetIdForApply, poiIds, systemProperty, subcategoryLevels) => {
        const data = await apiService.post(`/assets/${assetIdForApply}/pois/reassign-systems-batch`,
          { poiIds, systemProperty, subcategoryLevels });
        return { updated: data.updated || 0, unchanged: data.unchanged || 0, failed: data.failed || 0 };
      },
      applyLocations: async (assetIdForApply, poiIds, storeyProperty, spaceProperty) => {
        const data = await apiService.post(`/assets/${assetIdForApply}/pois/reassign-locations-batch`, {
          poiIds,
          storeyProperty: storeyProperty || null,
          spaceProperty: spaceProperty || null
        });
        return { updated: data.updated || 0, unchanged: data.unchanged || 0, failed: data.failed || 0 };
      },
      onApplySystemsError: (error, selectedCount) => {
        console.error('Failed to apply systems batch:', error);
        return { updated: 0, unchanged: 0, failed: selectedCount };
      },
      onApplyLocationsError: (error, selectedCount) => {
        console.error('Failed to apply locations batch:', error);
        return { updated: 0, unchanged: 0, failed: selectedCount };
      },
    },
  });

  const {
    showSetSystemsModal, setShowSetSystemsModal,
    systemsAvailableProperties, systemsPropertyValues,
    selectedSystemProperty, setSelectedSystemProperty,
    systemsSubcategoryLevels, setSystemsSubcategoryLevels,
    systemsSubcategorySearches, setSystemsSubcategorySearches,
    systemPropertySearch, setSystemPropertySearch,
    isLoadingSystemsProperties, isApplyingSystems,
    systemsResult, setSystemsResult,
    showSetLocationsModal, setShowSetLocationsModal,
    locationsAvailableProperties,
    selectedStoreyProperty, setSelectedStoreyProperty,
    selectedSpaceProperty, setSelectedSpaceProperty,
    storeyPropertySearch, setStoreyPropertySearch,
    spacePropertySearch, setSpacePropertySearch,
    isLoadingLocationsProperties, isApplyingLocations,
    locationsResult, setLocationsResult,
    handleOpenSetSystemsModal, handleApplySystemsBatch,
    addSystemsSubcategoryLevel, removeSystemsSubcategoryLevel,
    handleOpenSetLocationsModal, handleApplyLocationsBatch,
  } = poiBatch;

  // Auto-assign on sync config
  const [assignmentConfig, setAssignmentConfig] = useState<any>(null);
  const [loadingAssignmentConfig, setLoadingAssignmentConfig] = useState(false);

  // IFC Analysis and POI Generation state
  const [ifcAnalysisResult, setIfcAnalysisResult] = useState<any | null>(null);
  const [showIFCCategorySelector, setShowIFCCategorySelector] = useState(false);
  // setter removed with dead handleAnalyzeIFC — value stays false; UploadModals still takes the prop
  const [analyzingIFC] = useState(false);
  const [generatingPOIs, setGeneratingPOIs] = useState(false);
  const [ifcUploadedFile, setIfcUploadedFile] = useState<File | null>(null);

  // Property Set Configuration state
  const [showPropertySetModal, setShowPropertySetModal] = useState(false);
  const [propertySetConfig, setPropertySetConfig] = useState<{
    availableSets: string[];
    visibleSets: string[] | null;
  } | null>(null);
  const [propertySetSearch, setPropertySetSearch] = useState('');

  // Upload / conversion / RVT-translation-polling state + handlers — extracted to
  // src/pages/sites/useFileUpload (hook called below at the moved band's original slot,
  // so its useState/useRef/useEffect keep their execution order relative to other effects).

  // ACC (Autodesk Construction Cloud) state + handlers — extracted to src/pages/sites/useACCConnection
  const {
    showACCBrowserModal, setShowACCBrowserModal,
    accConnections, setAccConnections,
    accSelectedConnection, setAccSelectedConnection,
    accHubs, setAccHubs,
    accSelectedHub, setAccSelectedHub,
    accProjects, setAccProjects,
    accSelectedProject, setAccSelectedProject,
    accFolderContents, setAccFolderContents,
    accFolderPath, setAccFolderPath,
    accLoading, setAccLoading,
    accLinks, setAccLinks,
    accLinking, setAccLinking,
    accSelectedFileForLink, setAccSelectedFileForLink,
    accConversionType, setAccConversionType,
    accDisconnecting, setAccDisconnecting,
    assetsWithAccLinks, setAssetsWithAccLinks,
    showAccScopeModal, setShowAccScopeModal,
    accSelectedScope, setAccSelectedScope,
    showAccVersionModal, setShowAccVersionModal,
    accFileVersions, setAccFileVersions,
    accSelectedVersion, setAccSelectedVersion,
    accSelectedVersionUrn, setAccSelectedVersionUrn,
    accVersionsLoading, setAccVersionsLoading,
    accEnableMasterViews, setAccEnableMasterViews,
    accPendingLinkFile, setAccPendingLinkFile,
    accChangeVersionLink, setAccChangeVersionLink,
    accViewDirectMode, setAccViewDirectMode,
    // handlers
    handleConnectACC,
    handleAccScopeConfirm,
    handleDisconnectACC,
    handleACCSelectConnection,
    handleACCSelectHub,
    handleACCSelectProject,
    handleACCNavigateFolder,
    handleACCNavigateUp,
    handleACCLinkFile,
    handleVersionSelectAndLink,
    handleChangeVersionSave,
    handleChangeVersion,
    handleUseDirectView,
    handleACCViewDirect,
    handleVersionSelectAndViewDirect,
    handleACCAddNewConnection,
    loadACCLinks,
  } = useACCConnection({
    selectedAsset,
    loadAssets: () => loadAssets(),
    onAccLinksChanged,
    onOpenAutodeskViewer,
  });

  const [refreshingRvtLevels, setRefreshingRvtLevels] = useState<Record<string, boolean>>({});

  // Save phase selection to database via API
  const updateSelectedPhase = async (linkId: string, viewableGuid: string) => {
    try {
      await accService.updateLink(linkId, { selected_viewable_guid: viewableGuid });
      // Refresh ACC links (not connections) to update UI
      if (selectedAsset) {
        const links = await accService.getLinks(selectedAsset.id);
        setAccLinks(links);
      }
    } catch (e) {
      // The dropdown keeps showing the picked phase whether or not the server took it, so a
      // console line is invisible: the next model load silently uses the old viewable.
      console.error('Failed to save phase selection:', e);
      showErrorToast('Failed to save the phase selection. Please try again.');
    }
  };

  // Save room source phase selection to database via API
  const updateRoomSourcePhase = async (linkId: string, viewableGuid: string | null, viewableName: string | null) => {
    try {
      await accService.updateLink(linkId, {
        room_source_viewable_guid: viewableGuid,
        room_source_viewable_name: viewableName
      });
      // Refresh ACC links to update UI
      if (selectedAsset) {
        const links = await accService.getLinks(selectedAsset.id);
        setAccLinks(links);
      }
    } catch (e) {
      // Same as updateSelectedPhase — the UI reflects the choice regardless of the save.
      console.error('Failed to save room source phase selection:', e);
      showErrorToast('Failed to save the room source phase. Please try again.');
    }
  };

  // Load assets on component mount
  useEffect(() => {
    loadAssets();
    // Boot fetch, once. loadAssets is re-created every render and sets `assets`, so
    // depending on it would re-fetch the whole list on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadOnboardingSessions();
    // One-shot on mount. It used to be keyed on the role flag; that flag is now DERIVED from
    // what this call returns, so keying on it would mean never calling it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!isOnboardingManager) return;

    const hasActiveSessions = onboardingSessions.some((session) => session.status === 'draft' || session.status === 'processing');
    if (!hasActiveSessions) {
      return;
    }

    const timer = window.setInterval(() => {
      loadOnboardingSessions();
    }, 5000);

    return () => window.clearInterval(timer);
    // loadOnboardingSessions is re-created every render; depending on it would clear and
    // restart this 5s timer on every render, so the poll could never actually reach 5s.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnboardingManager, onboardingSessions]);

  // Load ACC connections when selected asset changes.
  // Reads the id rather than the asset object: the object is replaced by every loadAssets
  // refresh, and depending on it would re-fetch connections after each upload/sync.
  useEffect(() => {
    const assetId = selectedAsset?.id;
    if (assetId) {
      accService.getConnections(assetId).then(connections => {
        setAccConnections(connections);
      }).catch(err => {
        console.warn('Failed to load ACC connections for asset:', err);
        setAccConnections([]);
      });
    } else {
      setAccConnections([]);
    }
  }, [selectedAsset?.id, setAccConnections]);

  // Load assignment config when selected asset changes (for auto-assign toggle) — same
  // id-keying as above.
  useEffect(() => {
    const assetId = selectedAsset?.id;
    if (assetId) {
      setLoadingAssignmentConfig(true);
      apiService.get(`/assets/${assetId}/assignment-config`)
        .then((config: any) => {
          setAssignmentConfig(config);
        })
        .catch(() => {
          setAssignmentConfig(null);
        })
        .finally(() => {
          setLoadingAssignmentConfig(false);
        });
    } else {
      setAssignmentConfig(null);
    }
  }, [selectedAsset?.id]);

  // Handle modal blur effect - add/remove modal-open class to body
  useEffect(() => {
    const isAnyModalOpen = showCreateModal || showGuidedCreateModal || showEditModal || showUploadModal ||
                          showIFCUploadModal || showE57UploadModal || showGEOTIFFUploadModal ||
                          showPOIUploadModal || showPanoramaListModal || showPOIListModal ||
                          showIFCCategorySelector || showACCBrowserModal || showAccVersionModal;

    if (isAnyModalOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [showCreateModal, showGuidedCreateModal, showEditModal, showUploadModal, showIFCUploadModal,
      showE57UploadModal, showGEOTIFFUploadModal, showPOIUploadModal, showPanoramaListModal, showPOIListModal, showIFCCategorySelector, showACCBrowserModal, showAccVersionModal]);

  // Ensure selected asset is synchronized when assets are loaded
  useEffect(() => {
    if (assets.length > 0 && !selectedAsset) {
      const selectedId = assetService.getSelectedAssetId();
      if (selectedId) {
        const selected = assets.find(a => a.id === selectedId);
        if (selected) {
          setSelectedAsset(selected);
          onAssetSelected?.(selected);
        }
      }
    }
  }, [assets, selectedAsset, onAssetSelected]);

  const onboardingSessionBySiteId = onboardingSessions.reduce<Record<string, OnboardingSession>>((acc, session) => {
    acc[session.siteId] = session;
    return acc;
  }, {});

  const selectedAssetOnboardingSession = selectedAsset ? onboardingSessionBySiteId[selectedAsset.id] || null : null;

  useEffect(() => {
    // Keyed on ids, not objects: selectedAssetOnboardingSession is rebuilt by a reduce on
    // every render, so depending on it would re-force the tab on every render and fight
    // the user's own tab clicks.
    if (!selectedAsset?.id) {
      setSitesPanelTab('files');
      return;
    }

    if (!isOnboardingManager) {
      setSitesPanelTab('files');
      return;
    }

    if (selectedAssetOnboardingSession?.id && selectedAssetOnboardingSession?.status !== 'live') {
      setSitesPanelTab('onboarding');
      return;
    }

    setSitesPanelTab('files');
  }, [isOnboardingManager, selectedAsset?.id, selectedAssetOnboardingSession?.id, selectedAssetOnboardingSession?.status]);

  // Search assets when searchTerm changes (with debounce)
  useEffect(() => {
    if (!searchTerm.trim()) {
      setMatchingAssetIds(null);
      setSearchLoading(false);
      return;
    }

    setSearchLoading(true);
    const timer = setTimeout(async () => {
      try {
        const result = await apiService.get(`/assets/search?q=${encodeURIComponent(searchTerm)}`);
        setMatchingAssetIds(result.assetIds);
      } catch (error) {
        console.error('Search failed:', error);
        // On error, fallback to showing all (let client-side filter work)
        setMatchingAssetIds(null);
      } finally {
        setSearchLoading(false);
      }
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load assets from API
  const loadAssets = async () => {
    try {
      setLoading(true);
      const allAssets = await assetService.getAssets();
      setAssets(allAssets);

      // Load selected asset
      const selectedId = assetService.getSelectedAssetId();
      if (selectedId) {
        const selected = allAssets.find(a => a.id === selectedId);
        if (selected) {
          setSelectedAsset(selected);
          onAssetSelected?.(selected);
        }
      }
    } catch (error) {
      console.error('Failed to load assets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadOnboardingSessions = async () => {
    // No role guard: the SERVER decides who has onboarding work. GET /api/onboarding/projects
    // returns the sessions you created or were assigned to (everything, for an admin) and an
    // empty list for everyone else — which is exactly what isOnboardingManager now reads. A
    // guard here would be circular, and before migration 069 it was also wrong: it asked for
    // a global role that an assignee never held.
    try {
      setLoadingOnboardingSessions(true);
      const sessions = await onboardingService.listProjects();
      setOnboardingSessions(sessions);
    } catch (error) {
      console.error('Failed to load onboarding sessions:', error);
    } finally {
      setLoadingOnboardingSessions(false);
    }
  };

  // (onboardingSessionBySiteId / selectedAssetOnboardingSession moved above their first
  //  consumer in F-3 — they were declared BELOW an effect dep array that lists them,
  //  the documented babel inert-deps footgun)

  // Load ACC links when asset is selected
  useEffect(() => {
    if (selectedAsset) {
      loadACCLinks(selectedAsset.id);
    } else {
      setAccLinks([]);
    }
    // Once per asset: loadACCLinks is re-created every render and writes accLinks, so
    // depending on it would re-fetch the links on its own result.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAsset?.id]);

  // Load ACC link status for all assets to show badges
  useEffect(() => {
    if (assets.length === 0) return;

    const checkAllAssetsForAccLinks = async () => {
      const results = await Promise.allSettled(
        assets.map(async (asset) => {
          const links = await accService.getLinks(asset.id);
          return { assetId: asset.id, hasLinks: links.length > 0 };
        })
      );

      const assetsWithLinks = new Set<string>();
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.hasLinks) {
          assetsWithLinks.add(result.value.assetId);
        }
      });
      setAssetsWithAccLinks(assetsWithLinks);
    };

    checkAllAssetsForAccLinks();
  }, [assets, setAssetsWithAccLinks]);

  // Poll ACC sync status when there are pending/syncing links
  useEffect(() => {
    const hasPendingSync = accLinks.some(link =>
      link.sync_status === 'pending' || link.sync_status === 'syncing'
    );

    if (!hasPendingSync || !selectedAsset) return;

    const pollInterval = setInterval(async () => {
      // Only reload ACC links during polling
      const updatedLinks = await accService.getLinks(selectedAsset.id);
      setAccLinks(updatedLinks);

      // Check if any sync just completed - only then reload assets
      const justCompleted = accLinks.some(oldLink =>
        (oldLink.sync_status === 'pending' || oldLink.sync_status === 'syncing') &&
        updatedLinks.find(newLink => newLink.id === oldLink.id)?.sync_status === 'synced'
      );

      if (justCompleted) {
        loadAssets();
      }
    }, 5000); // Poll every 5 seconds

    return () => clearInterval(pollInterval);
    // loadAssets is re-created every render; depending on it would clear and restart this
    // 5s sync poll on every render. selectedAsset is read for its id only, which is listed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accLinks, selectedAsset?.id]);

  // Create new asset
  const handleCreateAsset = async () => {
    if (!newAsset.name.trim()) {
      showErrorToast(t('enterAssetName', 'Please enter an asset name'));
      return;
    }

    try {
      await assetService.createAsset(newAsset);
      await loadAssets();
      // A site creator just became site_admin of the new site; re-read their roles so the
      // screens scoped to the sites they administer (Users & Groups) include it now.
      if (!isAdmin) refreshRoles();
      setShowCreateModal(false);
      setNewAsset({ name: '', description: '' });
    } catch (error) {
      console.error('Failed to create asset:', error);
      showErrorToast('Failed to create asset. Please try again.');
    }
  };

  const handleCreateGuidedSite = async () => {
    if (!newAsset.name.trim()) {
      showErrorToast(t('enterAssetName', 'Please enter an asset name'));
      return;
    }

    try {
      setCreatingGuidedSite(true);
      const session = await onboardingService.createProject({
        siteName: newAsset.name.trim(),
        siteDescription: newAsset.description?.trim() || ''
      });

      const freshAssets = await assetService.getAssets();
      setAssets(freshAssets);

      const createdAsset = freshAssets.find((asset) => asset.id === session.siteId) || null;
      if (createdAsset) {
        handleSelectAsset(createdAsset);
      }

      setOnboardingSessions((prev) => {
        const existingIndex = prev.findIndex((item) => item.id === session.id);
        if (existingIndex === -1) {
          return [session, ...prev];
        }
        return prev.map((item) => item.id === session.id ? session : item);
      });
      setSitesPanelTab('onboarding');
      setShowGuidedCreateModal(false);
      setNewAsset({ name: '', description: '' });
    } catch (error) {
      console.error('Failed to create guided onboarding site:', error);
      showErrorToast('Failed to create guided onboarding site. Please try again.');
    } finally {
      setCreatingGuidedSite(false);
    }
  };

  // Update asset
  const handleEditAsset = async () => {
    if (!selectedAsset || !editAsset.name.trim()) {
      return;
    }

    try {
      // assetService.updateAsset RETURNS null on failure instead of throwing (AssetService.ts:96),
      // so the catch below never fires for a failed save. Without this check the modal closed and
      // the edit looked applied until a reload showed the old values.
      const saved = await assetService.updateAsset(selectedAsset.id, editAsset);
      if (!saved) {
        showErrorToast('Failed to update asset. Please try again.');
        return;
      }
      await loadAssets();
      const updatedAsset = assets.find(a => a.id === selectedAsset.id);
      if (updatedAsset) {
        setSelectedAsset(updatedAsset);
      }
      setShowEditModal(false);
    } catch (error) {
      console.error('Failed to update asset:', error);
      showErrorToast('Failed to update asset. Please try again.');
    }
  };

  // Delete asset
  const handleDeleteAsset = async (assetId: string) => {
    if (confirm(t('deleteConfirm', 'Are you sure you want to delete this asset?'))) {
      try {
        // Returns false rather than throwing (AssetService.ts:112) — same reason as the edit
        // path above: without checking, a refused delete silently re-rendered the site.
        const removed = await assetService.deleteAsset(assetId);
        if (!removed) {
          showErrorToast('Failed to delete asset. Please try again.');
          return;
        }
        await loadAssets();

        if (selectedAsset?.id === assetId) {
          setSelectedAsset(null);
        }
      } catch (error) {
        console.error('Failed to delete asset:', error);
        showErrorToast('Failed to delete asset. Please try again.');
      }
    }
  };

  // Select asset
  const handleSelectAsset = (asset: Asset) => {
    setSelectedAsset(asset);
    assetService.selectAsset(asset.id);
    onAssetSelected?.(asset);
  };

  const handleOnboardingSessionChange = (session: OnboardingSession) => {
    setOnboardingSessions((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === session.id);
      if (existingIndex === -1) {
        return [session, ...prev];
      }
      return prev.map((item) => item.id === session.id ? session : item);
    });
  };

  // Helper functions for per-asset, per-modal file selection
  const getSelectedFile = (assetId: string, fileType: string): File | null => {
    return selectedFiles[assetId]?.[fileType] || null;
  };

  const setSelectedFile = (assetId: string, fileType: string, file: File | null) => {
    setSelectedFiles(prev => ({
      ...prev,
      [assetId]: {
        ...prev[assetId],
        [fileType]: file
      }
    }));
  };

  // File upload / conversion / RVT-translation-polling subsystem — extracted to src/pages/sites/useFileUpload
  const {
    uploadProgress,
    isUploading,
    ifcConversionStatus,
    rvtTranslationStatus,
    handleFileUpload,
    handleSpecificFileUpload,
    handleFileDelete,
    handleOpenLocalRvt,
  } = useFileUpload({
    selectedAsset,
    setSelectedAsset,
    assets,
    setAssets,
    uploadFileType,
    getSelectedFile,
    setSelectedFile,
    setShowUploadModal,
    setShowIFCUploadModal,
    setShowE57UploadModal,
    setShowGEOTIFFUploadModal,
    setShowPOIUploadModal,
    selectedCompanionFiles,
    setSelectedCompanionFiles,
    getLocalRvtFiles,
    refreshToken,
    loadAssets,
    onAssetUpdated,
    onOpenAutodeskViewer,
  });

  // Local RVT helpers
  const updateLocalRvtSetting = async (fileId: string, settings: Record<string, any>) => {
    if (!selectedAsset) return;
    try {
      await apiService.patch(`/assets/${selectedAsset.id}/files/${fileId}/rvt-settings`, settings);
      // Refresh asset to get updated metadata
      const freshAssets = await assetService.getAssets();
      setAssets(freshAssets);
      const updated = freshAssets.find(a => a.id === selectedAsset.id);
      if (updated) setSelectedAsset(updated);
    } catch (err) {
      // No refresh happens on this path, so the toggle stays where the user left it and looks
      // saved — the mismatch only shows up on the next page load.
      console.error('Failed to update RVT settings:', err);
      showErrorToast('Failed to save the RVT setting. Please try again.');
    }
  };

  const setRvtLevelsRefreshing = (key: string, value: boolean) => {
    setRefreshingRvtLevels(prev => ({ ...prev, [key]: value }));
  };

  const refreshAccRvtLevels = async (linkId: string) => {
    if (!selectedAsset) return;
    const key = `acc:${linkId}`;
    setRvtLevelsRefreshing(key, true);
    try {
      await accService.refreshLevels(linkId);
      await loadACCLinks(selectedAsset.id);
    } catch (err: any) {
      console.error('Failed to refresh ACC RVT levels:', err);
      showErrorToast(`Failed to refresh levels: ${err.message || err}`);
    } finally {
      setRvtLevelsRefreshing(key, false);
    }
  };

  const refreshLocalRvtLevels = async (fileId: string) => {
    if (!selectedAsset) return;
    const key = `local:${fileId}`;
    setRvtLevelsRefreshing(key, true);
    try {
      await apiService.post(`/assets/${selectedAsset.id}/files/${fileId}/rvt-refresh-levels`, {});

      const freshAssets = await assetService.getAssets();
      setAssets(freshAssets);
      const updated = freshAssets.find(a => a.id === selectedAsset.id);
      if (updated) setSelectedAsset(updated);
    } catch (err: any) {
      console.error('Failed to refresh local RVT levels:', err);
      showErrorToast(`Failed to refresh levels: ${err.message || err}`);
    } finally {
      setRvtLevelsRefreshing(key, false);
    }
  };

  // Load panoramas for selected asset
  const loadPanoramas = async (assetId: string) => {
    if (!assetId) return;

    setLoadingPanoramas(true);

    try {
      const data = await apiService.get(`/assets/${assetId}/panoramas`);
      setPanoramas(data.panoramas || []);

    } catch (error) {
      console.error('❌ Failed to load panoramas:', error);
      setPanoramas([]);
      // Show user-friendly error message
      showErrorToast(`Failed to load panoramas: ${error.message}`);
    } finally {
      setLoadingPanoramas(false);
    }
  };

  // Handle panorama list modal opening
  const handleShowPanoramaList = async () => {
    if (selectedAsset) {
      setShowPanoramaListModal(true);
      await loadPanoramas(selectedAsset.id);
    }
  };

  // Load POIs for selected asset
  const loadPOIs = async (assetId: string) => {
    if (!assetId) return;
    setLoadingPOIs(true);

    try {
      const data = await apiService.get(`/assets/${assetId}/pois`);
      setPois(data || []);
    } catch (error) {
      console.error('❌ Failed to load POIs:', error);
      setPois([]);
      showErrorToast(`Failed to load assets: ${error.message}`);
    } finally {
      setLoadingPOIs(false);
    }
  };

  // Handle POI list modal opening
  const handleShowPOIList = async () => {
    if (selectedAsset) {
      setShowPOIListModal(true);
      await loadPOIs(selectedAsset.id);
    }
  };

  // Delete individual POI
  const handleDeletePOI = async (poiId: string) => {
    if (!selectedAsset || !confirm('Are you sure you want to delete this asset?')) return;

    setDeletingPOI(poiId);
    try {
      // batch-delete with a single id — there is no DELETE .../pois/:poiId route on the server
      // (only .../pois to clear them all, and .../pois/batch-delete), so the per-row button
      // 404'd on every click. Every other single-POI delete in the app already goes through
      // batch-delete: useAddIoTDevice.ts, UnifiedPOITable, AutodeskViewer.
      await apiService.delete(`/assets/${selectedAsset.id}/pois/batch-delete`, { poiIds: [poiId] });

      // Remove the deleted POI from the list
      setPois(pois.filter(poi => poi.id !== poiId));

      // Reload assets to update the POI count
      await loadAssets();

      logger.debug('✅ POI deleted successfully');
    } catch (error) {
      console.error('❌ Failed to delete POI:', error);
      showErrorToast(`Failed to delete asset: ${error.message}`);
    } finally {
      setDeletingPOI(null);
    }
  };

  // Delete all POIs
  const handleDeleteAllPOIs = async () => {
    if (!selectedAsset || !confirm(`Are you sure you want to delete ALL ${pois.length} assets for this site? This action cannot be undone.`)) return;

    setDeletingAllPOIs(true);
    try {
      await apiService.delete(`/assets/${selectedAsset.id}/pois`);

      // Clear the POIs list
      setPois([]);

      // Close the modal
      setShowPOIListModal(false);

      // Reload assets to update the POI count
      await loadAssets();

      logger.debug('✅ All POIs deleted successfully');
      showSuccessToast('All assets have been deleted successfully.');
    } catch (error) {
      console.error('❌ Failed to delete all POIs:', error);
      showErrorToast(`Failed to delete all assets: ${error.message}`);
    } finally {
      setDeletingAllPOIs(false);
    }
  };

  // Edit mode handlers
  const handleEnterEditMode = () => {
    // Initialize edited POIs with ALL current values
    const initialEdits: Record<string, any> = {};
    pois.forEach(poi => {
      // Parse coordinates from various sources
      let modelCoords = { x: '', y: '', z: '' };
      let normalized2dCoords = { x: '', y: '', z: '' };
      try {
        if (poi.model_coordinates) {
          const coords = typeof poi.model_coordinates === 'string' ? JSON.parse(poi.model_coordinates) : poi.model_coordinates;
          modelCoords = { x: coords.x || '', y: coords.y || '', z: coords.z || '' };
        }
      } catch (e) {}
      try {
        if (poi.normalized_2d_coordinates) {
          const coords = typeof poi.normalized_2d_coordinates === 'string' ? JSON.parse(poi.normalized_2d_coordinates) : poi.normalized_2d_coordinates;
          normalized2dCoords = { x: coords.x || '', y: coords.y || '', z: coords.z || '' };
        }
      } catch (e) {}

      initialEdits[poi.id] = {
        title: poi.title || '',
        subcategory: poi.subcategory || '',
        x: poi.x || '',
        y: poi.y || '',
        z: poi.z || '',
        model_coordinates_x: modelCoords.x,
        model_coordinates_y: modelCoords.y,
        model_coordinates_z: modelCoords.z,
        normalized_2d_x: normalized2dCoords.x,
        normalized_2d_y: normalized2dCoords.y,
        normalized_2d_z: normalized2dCoords.z,
        poi_type_name: poi.poi_type_name || '',
        poi_group_name: poi.poi_group_name || '',
        ifc_category: poi.ifc_category || '',
        importance: poi.importance || 1,
        visibility_check: poi.visibility_check !== false,
        poi_id: poi.poi_id || '',
        ifc_element_guid: poi.ifc_element_guid || '',
        poi_type_icon: poi.poi_type_icon || poi.icon || '📍'
      };
    });
    setEditedPOIs(initialEdits);
    setModifiedPOIs(new Set()); // Start fresh - no POIs modified yet
    setIsEditMode(true);
  };

  const handleCancelEditMode = () => {
    setEditedPOIs({});
    setModifiedPOIs(new Set());
    setIsEditMode(false);
  };

  const handlePOIFieldChange = (poiId: string, field: string, value: any) => {
    // If this POI is selected and there are multiple selections, apply to all selected
    if (selectedPOIs.has(poiId) && selectedPOIs.size > 1) {
      // Apply to all selected POIs
      setEditedPOIs(prev => {
        const updated = { ...prev };
        selectedPOIs.forEach(selectedId => {
          if (updated[selectedId]) {
            updated[selectedId] = { ...updated[selectedId], [field]: value };
          }
        });
        return updated;
      });
      // Track all selected POIs as modified
      setModifiedPOIs(prev => {
        const newSet = new Set(prev);
        selectedPOIs.forEach(selectedId => newSet.add(parseInt(selectedId, 10)));
        return newSet;
      });
    } else {
      // Single POI edit
      setEditedPOIs(prev => ({
        ...prev,
        [poiId]: {
          ...prev[poiId],
          [field]: value
        }
      }));
      // Track that this POI was modified
      setModifiedPOIs(prev => new Set(prev).add(parseInt(poiId, 10)));
    }
  };

  const handleSaveAllEdits = async () => {
    if (!selectedAsset) {
      console.error('Cannot save: selectedAsset is null');
      showErrorToast('Cannot save: No site selected. Please select a site first.');
      return;
    }

    // Only save POIs that were actually modified
    if (modifiedPOIs.size === 0) {
      showErrorToast('No changes to save');
      setIsEditMode(false);
      setEditedPOIs({});
      return;
    }

    setSavingPOIs(true);
    try {
      // Filter to only include POIs that were actually modified
      const poisToUpdate = Object.entries(editedPOIs)
        .filter(([poiId]) => modifiedPOIs.has(parseInt(poiId, 10)))
        .map(([poiId, edits]) => ({
          id: parseInt(poiId, 10),
          ...edits
        }));

      // Batch update only modified POIs
      const result = await apiService.put(`/assets/${selectedAsset.id}/pois/batch-update`, { pois: poisToUpdate });
      logger.debug('Saved POIs:', result);

      // Reload POIs to get updated data (best-effort — the save already succeeded)
      const poisData = await apiService.get(`/assets/${selectedAsset.id}/pois`).catch(() => null);
      if (poisData) {
        setPois(poisData);
      }

      setIsEditMode(false);
      setEditedPOIs({});
      setModifiedPOIs(new Set());
      showSuccessToast(`Successfully saved ${poisToUpdate.length} asset(s)`);
    } catch (error) {
      console.error('Failed to save POIs:', error);
      showErrorToast(`Failed to save: ${error.message}`);
    } finally {
      setSavingPOIs(false);
    }
  };

  // Handler: Open Set Systems modal
  // Set Systems / Set Locations open+apply+subcategory handlers come from
  // useViewerPOIBatchActions (destructured above); the transport adapter preserves this
  // page's exact behavior.

  const handleIFCCategoriesSelected = async (selectedCategories: string[]) => {
    logger.debug(`🎯 DEBUG: handleIFCCategoriesSelected called with:`, {
      selectedCategories,
      hasSelectedAsset: !!selectedAsset,
      hasIfcFile: !!ifcUploadedFile,
      hasAnalysisResult: !!ifcAnalysisResult
    });

    if (!selectedAsset || !ifcUploadedFile) {
      logger.debug(`❌ DEBUG: Missing requirements - selectedAsset: ${!!selectedAsset}, ifcUploadedFile: ${!!ifcUploadedFile}`);
      return;
    }

    setGeneratingPOIs(true);
    try {
      // Dynamic import keeps the web-ifc chain out of the boot bundle (P4).
      const { IFCToPOIConverter } = await import('../services/IFCToPOIConverter');
      const converter = IFCToPOIConverter.getInstance();
      const options = {
        assetId: selectedAsset.id,
        sourceFileName: ifcUploadedFile.name,
        generateNames: true,
        importanceLevel: 5,
        visibilityDefault: true,
        sharedReferencePoint: ifcAnalysisResult.sharedReferencePoint,
        analysisResult: ifcAnalysisResult
      };

      // Extract elements from selected categories
      const selectedElements = ifcAnalysisResult.categories
        .filter(category => selectedCategories.includes(category.name))
        .flatMap(category => category.elements);

      logger.debug(`✅ Selected ${selectedElements.length} navigable elements for POI creation`);

      const result = await converter.convertElementsToPOIs(selectedElements, options);

      if (result.success) {
        // Store shared reference point as asset metadata
        let referencePointStored = true;
        try {
          const currentCustomData = parseAssetCustomData(selectedAsset);

          const updatedCustomData = {
            ...currentCustomData,
            ifc_shared_reference_point: ifcAnalysisResult.sharedReferencePoint,
            ifc_analysis_timestamp: new Date().toISOString(),
            ifc_source_file: ifcUploadedFile.name
          };

          const metadataPayload = {
            ...selectedAsset,
            custom_data: updatedCustomData
          };
          // Returns null instead of throwing (AssetService.ts:96), so the catch below never saw
          // a refused save — the reference point is what aligns 2D/3D, and losing it produced
          // "the map is offset" reports long after this import looked successful.
          referencePointStored = Boolean(await assetService.updateAsset(selectedAsset.id, metadataPayload));

          logger.debug(`🎯 ASSET METADATA: Stored shared reference point for asset ${selectedAsset.id}:`, ifcAnalysisResult.sharedReferencePoint);
        } catch (metadataError) {
          console.error('❌ Failed to store shared reference as asset metadata:', metadataError);
          referencePointStored = false;
        }

        showSuccessToast(`Successfully created ${result.poisCreated} assets from IFC elements!`);
        if (!referencePointStored) {
          // Partial success: the POIs exist, the alignment metadata does not.
          showErrorToast('Assets were created, but the IFC shared reference point could not be saved. 2D/3D alignment may be off until you re-run the import.');
        }
        // Refresh POI list if modal is open
        if (showPOIListModal) {
          await loadPOIs(selectedAsset.id);
        }
        onAssetUpdated?.(selectedAsset.id);
      } else {
        showErrorToast(`Asset generation completed with ${result.errors.length} errors. Created ${result.poisCreated} assets.`);
      }
    } catch (error) {
      console.error('POI generation failed:', error);
      showErrorToast('Failed to generate assets from IFC elements. Please try again.');
    } finally {
      setGeneratingPOIs(false);
      setShowIFCCategorySelector(false);
      setIfcAnalysisResult(null);
      setIfcUploadedFile(null);
    }
  };

  const handleIFCCategoryCancelSelection = () => {
    setShowIFCCategorySelector(false);
    setIfcAnalysisResult(null);
    setIfcUploadedFile(null);
  };

  const handleLocationsCreated = (spatialElements: IFCElementInfo[]) => {
    logger.debug('✅ Locations created successfully from', spatialElements.length, 'spatial elements');
    showSuccessToast(`Successfully created ${spatialElements.length} locations! Check the Locations page to view them.`);

    // Keep the IFC Category Selector open so user can continue with POI extraction
    // User can manually close it when done by clicking Cancel
  };

  // Handle setting default IFC file
  const handleSetDefaultIFC = async (ifcFile: AssetFile) => {
    if (!selectedAsset) return;

    try {
      const success = await fileService.setDefaultFile(selectedAsset.id, ifcFile.id);
      if (success) {
        // Update the UI locally without full reload to prevent page scroll
        const updatedAssets = assets.map(asset => {
          if (asset.id === selectedAsset.id && asset.files.ifc && Array.isArray(asset.files.ifc)) {
            // Update the IFC files array - set only the clicked file as default
            const updatedIfcFiles = asset.files.ifc.map(ifc => ({
              ...ifc,
              isDefault: ifc.id === ifcFile.id
            }));
            return {
              ...asset,
              files: {
                ...asset.files,
                ifc: updatedIfcFiles
              }
            };
          }
          return asset;
        });

        // Update state locally without triggering loading state
        setAssets(updatedAssets);
        const updatedAsset = updatedAssets.find(a => a.id === selectedAsset.id);
        if (updatedAsset) {
          setSelectedAsset(updatedAsset);
        }

        logger.debug('✅ Default IFC file set successfully');
      } else {
        showErrorToast('Failed to set default IFC file. Please try again.');
      }
    } catch (error) {
      console.error('❌ Failed to set default IFC file:', error);
      showErrorToast(`Failed to set default IFC file: ${error.message}`);
    }
  };

  // Property Set Configuration handlers
  const handleConfigurePropertySets = () => {
    if (!selectedAsset) return;

    // Parse custom_data to get existing property set config
    let config = { availableSets: [], visibleSets: null };
    if (selectedAsset.custom_data) {
      try {
        const customData = parseAssetCustomData(selectedAsset);
        if (customData.propertySetConfig) {
          config = customData.propertySetConfig;
        }
      } catch (e) {
        console.warn('Failed to parse custom_data:', e);
      }
    }

    if (config.availableSets.length === 0) {
      showErrorToast('No property sets discovered yet. Please extract assets first to populate the list.');
      return;
    }

    setPropertySetConfig(config);
    setShowPropertySetModal(true);
  };

  const handleTogglePropertySet = (setName: string) => {
    if (!propertySetConfig) return;

    let newVisibleSets: string[] | null;

    if (propertySetConfig.visibleSets === null) {
      // Currently showing all - remove this one
      newVisibleSets = propertySetConfig.availableSets.filter(s => s !== setName);
    } else if (propertySetConfig.visibleSets.includes(setName)) {
      // Remove from visible
      newVisibleSets = propertySetConfig.visibleSets.filter(s => s !== setName);
      // If all are now hidden, keep as empty array
    } else {
      // Add to visible
      newVisibleSets = [...propertySetConfig.visibleSets, setName];
      // If all are now visible, set to null
      if (newVisibleSets.length === propertySetConfig.availableSets.length) {
        newVisibleSets = null;
      }
    }

    setPropertySetConfig({
      ...propertySetConfig,
      visibleSets: newVisibleSets
    });
  };

  const handleSavePropertySetConfig = async () => {
    if (!selectedAsset || !propertySetConfig) return;

    try {
      const existingCustomData = parseAssetCustomData(selectedAsset);

      const updatedCustomData = {
        ...existingCustomData,
        propertySetConfig
      };

      await apiService.patch(`/assets/${selectedAsset.id}`, {
        custom_data: JSON.stringify(updatedCustomData)
      });

      // Update local state
      setSelectedAsset({
        ...selectedAsset,
        custom_data: updatedCustomData
      });

      setShowPropertySetModal(false);
      logger.debug('Property set config saved:', propertySetConfig);
    } catch (error) {
      console.error('Failed to save property set config:', error);
      showErrorToast('Failed to save property set configuration');
    }
  };

  const handleSelectAllPropertySets = () => {
    if (!propertySetConfig) return;
    setPropertySetConfig({
      ...propertySetConfig,
      visibleSets: null  // null means show all
    });
  };

  const handleUnselectAllPropertySets = () => {
    if (!propertySetConfig) return;
    setPropertySetConfig({
      ...propertySetConfig,
      visibleSets: []  // empty array means show none
    });
  };

  const getE57Sources = (asset: Asset | null | undefined): AssetFile[] => {
    const sources = (asset?.files as any)?.e57Sources;
    if (Array.isArray(sources)) {
      return sources;
    }
    return asset?.files?.e57 ? [asset.files.e57] : [];
  };

  const getE57SourceCustomData = (asset: Asset | null | undefined, source: AssetFile): Record<string, any> => {
    const customData = parseAssetCustomData(asset || null);
    const sourceMap = customData.e57_sources || customData.e57Sources || {};
    const sourceKeys = [
      source.sourceFileId,
      source.id,
      source.metadata?.sourceFileId
    ]
      .filter((key) => key !== undefined && key !== null)
      .map((key) => String(key));

    for (const key of sourceKeys) {
      if (sourceMap[key] && typeof sourceMap[key] === 'object') {
        return sourceMap[key];
      }
    }

    return {};
  };

  const getE57SourceKey = (source: AssetFile): string => {
    const key = source.sourceFileId ?? source.metadata?.sourceFileId ?? source.id;
    return String(key);
  };

  const updateE57SourceCustomData = async (
    source: AssetFile,
    updater: (currentSourceData: Record<string, any>) => Record<string, any>
  ): Promise<boolean> => {
    return updateSelectedAssetCustomData((currentCustomData) => {
      const sourceKey = getE57SourceKey(source);
      const currentSources = currentCustomData.e57_sources || currentCustomData.e57Sources || {};
      const currentSourceData = currentSources[sourceKey] && typeof currentSources[sourceKey] === 'object'
        ? currentSources[sourceKey]
        : {};

      return {
        ...currentCustomData,
        e57_sources: {
          ...currentSources,
          [sourceKey]: {
            ...currentSourceData,
            id: sourceKey,
            fileName: source.fileName,
            updatedAt: new Date().toISOString(),
            ...updater(currentSourceData)
          }
        }
      };
    });
  };

  const getE57SphereNavigationSettings = (
    asset: Asset | null | undefined,
    source: AssetFile
  ): E57SphereNavigationSettings => {
    const customSource = getE57SourceCustomData(asset, source);
    const settings = customSource.sphereNavigation || customSource.sphere_navigation || {};

    const coercePositiveNumber = (value: unknown, fallback: number): number => {
      const parsed = Number(value);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    };

    const placementMode = settings.placementMode === 'true_vector' || settings.placement_mode === 'true_vector'
      ? 'true_vector'
      : 'legacy';

    return {
      placementMode,
      scaleToMeters: coercePositiveNumber(settings.scaleToMeters ?? settings.scale_to_meters, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.scaleToMeters),
      verticalScaleToMeters: coercePositiveNumber(settings.verticalScaleToMeters ?? settings.vertical_scale_to_meters, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.verticalScaleToMeters),
      cameraHeightAboveFloorMeters: coercePositiveNumber(settings.cameraHeightAboveFloorMeters ?? settings.camera_height_above_floor_meters, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.cameraHeightAboveFloorMeters),
      maxDistanceMeters: coercePositiveNumber(settings.maxDistanceMeters ?? settings.max_distance_meters, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.maxDistanceMeters),
      markerNearSize: coercePositiveNumber(settings.markerNearSize ?? settings.marker_near_size, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.markerNearSize),
      markerFarSize: coercePositiveNumber(settings.markerFarSize ?? settings.marker_far_size, DEFAULT_E57_SPHERE_NAVIGATION_SETTINGS.markerFarSize)
    };
  };

  const updateE57SphereNavigationSettings = async (
    source: AssetFile,
    patch: Partial<E57SphereNavigationSettings>
  ) => {
    await updateE57SourceCustomData(source, (currentSourceData) => {
      const currentSettings = getE57SphereNavigationSettings(selectedAsset, source);

      return {
        ...currentSourceData,
        sphereNavigation: {
          ...currentSettings,
          ...patch,
          updatedAt: new Date().toISOString()
        }
      };
    });
  };

  const updateE57ManualCoordinateReference = async (source: AssetFile, epsgCode: string) => {
    const cleanCode = epsgCode.replace(/[^\d]/g, '').trim();

    await updateE57SourceCustomData(source, (currentSourceData) => {
      const nextSourceData = { ...currentSourceData };

      if (!cleanCode) {
        delete nextSourceData.coordinateReference;
        return nextSourceData;
      }

      return {
        ...nextSourceData,
        coordinateReference: {
          status: 'manual',
          source: 'manual',
          authority: 'EPSG',
          code: cleanCode,
          label: `EPSG:${cleanCode}`,
          rawCoordinateMetadataPresent: Boolean(
            source.metadata?.coordinateMetadata || currentSourceData.coordinateMetadata
          ),
          updatedAt: new Date().toISOString()
        }
      };
    });
  };

  const getCoordinateReferenceFromMetadata = (coordinateMetadata: unknown): E57CoordinateReferenceInfo | null => {
    if (typeof coordinateMetadata !== 'string' || coordinateMetadata.trim().length === 0) {
      return null;
    }

    const authorityMatch =
      coordinateMetadata.match(/(?:AUTHORITY|ID)\s*\[\s*["']EPSG["']\s*,\s*["']?(\d{3,6})["']?\s*\]/i) ||
      coordinateMetadata.match(/\bEPSG\s*[:",\s]+\s*(\d{3,6})\b/i);
    const code = authorityMatch?.[1] || null;

    return {
      status: 'declared',
      source: 'e57.coordinateMetadata',
      label: code ? `EPSG:${code}` : 'Declared in E57 coordinateMetadata',
      authority: code ? 'EPSG' : null,
      code,
      rawCoordinateMetadataPresent: true
    };
  };

  const getE57CoordinateReferenceDisplay = (
    asset: Asset | null | undefined,
    source: AssetFile
  ): E57CoordinateReferenceDisplay => {
    const sourceMetadata = source.metadata || {};
    const customSource = getE57SourceCustomData(asset, source);
    const coordinateReference = (
      sourceMetadata.coordinateReference ||
      sourceMetadata.coordinate_reference ||
      customSource.coordinateReference ||
      customSource.coordinate_reference
    ) as E57CoordinateReferenceInfo | undefined;

    if (coordinateReference && typeof coordinateReference === 'object') {
      const label = coordinateReference.label ||
        (coordinateReference.authority && coordinateReference.code
          ? `${coordinateReference.authority}:${coordinateReference.code}`
          : null);

      if (label) {
        const sourceLabel = coordinateReference.source === 'manual'
          ? 'manual'
          : coordinateReference.source;

        return {
          label: `CRS: ${label}`,
          detail: sourceLabel ? `source: ${sourceLabel}` : undefined
        };
      }

      if (coordinateReference.status === 'missing') {
        return { label: 'CRS: not declared in E57' };
      }
    }

    const metadataCoordinateReference = getCoordinateReferenceFromMetadata(
      sourceMetadata.coordinateMetadata || customSource.coordinateMetadata
    );
    if (metadataCoordinateReference?.label) {
      return {
        label: `CRS: ${metadataCoordinateReference.label}`,
        detail: 'source: e57.coordinateMetadata'
      };
    }

    const hasRecordedCoordinateMetadata =
      Object.prototype.hasOwnProperty.call(sourceMetadata, 'coordinateMetadata') ||
      Object.prototype.hasOwnProperty.call(customSource, 'coordinateMetadata');

    return hasRecordedCoordinateMetadata
      ? { label: 'CRS: not declared in E57' }
      : { label: 'CRS: not recorded' };
  };

  const getTotalE57PanoramaCount = (asset: Asset | null | undefined): number => {
    const sources = getE57Sources(asset);
    if (sources.length > 0) {
      return sources.reduce((total, source) => total + (source.panoramaCount || 0), 0);
    }
    return asset?.files?.e57?.panoramaCount || 0;
  };

  // Helper function to count actual files in an asset
  const countAssetFiles = (asset: Asset): number => {
    let count = 0;
    if (asset.files) {
      // IFC files are an array
      if (asset.files.ifc && Array.isArray(asset.files.ifc)) {
        count += asset.files.ifc.length;
      }
      count += getLocalRvtFiles(asset).length;
      // Other file types are single files
      const e57Sources = getE57Sources(asset);
      count += e57Sources.length;
      if (asset.files.poi) count += 1;
      if (asset.files.geotiff) count += 1;
      if (asset.files.dwg) count += 1;
      if (asset.files.dxf) count += 1;
    }
    return count;
  };

  // Filter assets - use server-side search results if available
  const filteredAssets = assets.filter(asset => {
    // If no search term, show all
    if (!searchTerm.trim()) return true;

    // If server-side search returned results, use them
    if (matchingAssetIds !== null) {
      return matchingAssetIds.includes(asset.id);
    }

    // Fallback to client-side search (during loading or error)
    return asset.name?.toLowerCase().includes(searchTerm?.toLowerCase() || '') ||
           asset.description?.toLowerCase().includes(searchTerm?.toLowerCase() || '');
  });

  const selectedAssetHasRVTLink = accLinks.some(link => link.acc_file_name?.toLowerCase().endsWith('.rvt')) || getLocalRvtFiles(selectedAsset).length > 0;
  const panoramaToRVTTransform = getPanoramaToRVTTransform(selectedAsset);
  const panoramaAlignmentDiagnostics = coordinateTransformService.getCoordinateAlignmentDiagnostics({ asset: selectedAsset });
  const formatCoordinateValue = (value: any): string => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed.toFixed(3) : 'n/a';
  };
  const formatCoordinatePoint = (point: any): string => {
    if (!point) return 'n/a';
    return `${formatCoordinateValue(point.x)}, ${formatCoordinateValue(point.y)}, ${formatCoordinateValue(point.z)}`;
  };
  const formatCoordinateBounds = (bounds: any): string => {
    if (!bounds?.min || !bounds?.max) return 'n/a';
    return `${formatCoordinatePoint(bounds.min)} → ${formatCoordinatePoint(bounds.max)}`;
  };
  const transformPanelStyle: React.CSSProperties = {
    marginTop: '10px',
    padding: '14px',
    background: 'var(--background-secondary)',
    border: '1px solid var(--border-color)',
    borderRadius: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    width: '100%',
    boxSizing: 'border-box',
    overflow: 'hidden'
  };
  const transformFieldGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '10px',
    width: '100%'
  };
  const transformFieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
    minWidth: 0
  };
  const transformInputStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 0,
    padding: '6px 8px',
    boxSizing: 'border-box'
  };
  const transformToggleGridStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '10px',
    width: '100%'
  };
  const selectedAssetE57Sources = getE57Sources(selectedAsset);
  const selectedAssetE57PanoramaCount = getTotalE57PanoramaCount(selectedAsset);

  if (loading) {
    return (
      <div className="dt-body" dir={currentLanguage === 'he' ? 'rtl' : 'ltr'}>
        <div className="dt-statebox">{t('sitesLoadingAssets', 'Loading assets...')}</div>
      </div>
    );
  }

  // Someone who can only look at sites gets the sites and a way into each — none of the
  // file workshop below (src/pages/sites/SiteTiles.tsx says why).
  if (isReadOnlySitesVisitor({ isAdmin, hasOnboardingWork: isOnboardingManager, canCreateSites, sites: assets, canManageSite: canManageAsset })) {
    return (
      <SiteTiles
        sites={filteredAssets}
        totalSites={assets.length}
        currentSiteId={selectedAsset?.id ?? null}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        onOpenSite={(site) => (onOpenSiteViewer ? onOpenSiteViewer(site.id) : handleSelectAsset(site))}
        t={t}
        dir={currentLanguage === 'he' ? 'rtl' : 'ltr'}
      />
    );
  }

  return (
    <div className="dt-body" dir={currentLanguage === 'he' ? 'rtl' : 'ltr'}>
      <div className="dt-phead">
        <div className="pic">
          <svg viewBox="0 0 24 24"><rect x="4" y="3" width="16" height="18" rx="1"/><path d="M9 21v-4h6v4"/><path d="M8 7h.01M12 7h.01M16 7h.01M8 11h.01M12 11h.01M16 11h.01"/></svg>
        </div>
        <div>
          <h2>{t('sitesManagement', 'Sites Management')}</h2>
          <div className="sub">{assets.length} {assets.length === 1 ? 'site' : 'sites'}</div>
        </div>
        <div className="pa">
          <input
            type="text"
            placeholder={t('searchSites', 'Search sites...')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dt-field"
            style={{ width: '190px' }}
          />
          {isOnboardingManager && (
            <button onClick={() => setShowGuidedCreateModal(true)} className="dt-btn">
              Guided Onboarding
            </button>
          )}
          {/* Admins, and the site creators Idan named (migration 072) — the server's rule */}
          {(isAdmin || canCreateSites) && (
            <button onClick={() => setShowCreateModal(true)} className="dt-btn pri">
              {t('createSite', 'Create Site')}
            </button>
          )}
        </div>
      </div>

      <div className="dt-cols" style={{ gridTemplateColumns: '260px 1fr' }}>
        {/* Sites List */}
        <div className="dt-card">
          <div className="c" style={{ padding: '8px' }}>
          {filteredAssets.length === 0 ? (
            <div className="dt-statebox">
              {t('noSites', 'No sites found')}
            </div>
          ) : (
            filteredAssets.map(asset => {
              const assetOnboardingSession = onboardingSessionBySiteId[asset.id] || null;
              return (
                <div
                  key={asset.id}
                  className={`dt-stlrow${selectedAsset?.id === asset.id ? ' sel' : ''}`}
                  onClick={() => handleSelectAsset(asset)}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{asset.name}</span>
                      {assetOnboardingSession && renderOnboardingBadge(assetOnboardingSession.status)}
                    </div>
                    {asset.description && (
                      <div style={{ fontWeight: 400, fontSize: '.72rem', color: 'var(--dt-text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.description}
                      </div>
                    )}
                    <div style={{ fontWeight: 400, fontSize: '.68rem', color: 'var(--dt-text-3)', marginTop: '2px' }}>
                      {countAssetFiles(asset)} files
                      {asset.locationCount !== undefined && asset.locationCount > 0 && ` · ${asset.locationCount} locations`}
                      {asset.poiCount !== undefined && asset.poiCount > 0 && ` · ${asset.poiCount} assets`}
                      {' · '}{new Date(asset.createdDate).toLocaleDateString()}
                    </div>
                  </div>
                  {assetsWithAccLinks.has(asset.id) && (
                    <span className="cnt" title="Synced with Autodesk Construction Cloud" style={{ color: 'var(--dt-accent-strong)', fontWeight: 700 }}>
                      ACC
                    </span>
                  )}
                </div>
              );
            })
          )}
          </div>
        </div>

        {/* Asset Details */}
        {selectedAsset && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', minWidth: 0 }}>
            {/* Site-header card */}
            <div className="dt-card">
              <div className="c" style={{ padding: '14px 16px', display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <div style={{ fontWeight: 700, fontSize: '1.05rem', color: 'var(--dt-text)' }}>{selectedAsset.name}</div>
                    {selectedAssetOnboardingSession && renderOnboardingBadge(selectedAssetOnboardingSession.status)}
                  </div>
                  <div style={{ fontSize: '.82rem', color: 'var(--dt-text-3)', marginTop: '2px' }}>
                    {selectedAsset.description ? `${selectedAsset.description} · ` : ''}
                    {selectedAsset.locationCount !== undefined && selectedAsset.locationCount > 0 ? `${selectedAsset.locationCount} locations · ` : ''}
                    {selectedAsset.poiCount !== undefined && selectedAsset.poiCount > 0 ? `${selectedAsset.poiCount} assets · ` : ''}
                    created {new Date(selectedAsset.createdDate).toLocaleDateString()}
                  </div>
                </div>
                {selectedAsset.permission_level === 'write' && (
                  <span style={{ marginInlineStart: 'auto', display: 'flex', gap: '6px' }}>
                    <button
                      onClick={() => {
                        setEditAsset({ name: selectedAsset.name, description: selectedAsset.description });
                        setShowEditModal(true);
                      }}
                      className="dt-btn sm"
                      title="Edit Asset"
                    >
                      Rename
                    </button>
                    {/* DELETE IS ADMIN-ONLY, which is what the server has always enforced:
                        the route reads `isAssetOwner || admin`, and isAssetOwner is set true
                        only in the admin branch — so even the site's creator was refused.
                        Every writer was shown a button that answered 403. Product decision,
                        2026-09-22: the screen narrows to match the rule. */}
                    {isAdmin && (
                      <button
                        onClick={() => handleDeleteAsset(selectedAsset.id)}
                        className="dt-btn sm dt-dngr"
                        title="Delete Asset"
                      >
                        Delete
                      </button>
                    )}
                  </span>
                )}
              </div>
            </div>

            {isOnboardingManager && (
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <div className="dt-seg">
                  <button
                    type="button"
                    onClick={() => setSitesPanelTab('files')}
                    className={sitesPanelTab === 'files' ? 'on' : ''}
                  >
                    Site Files
                  </button>
                  <button
                    type="button"
                    onClick={() => setSitesPanelTab('onboarding')}
                    className={sitesPanelTab === 'onboarding' ? 'on' : ''}
                  >
                    Onboarding
                  </button>
                </div>
                {loadingOnboardingSessions && (
                  <span style={{ color: 'var(--dt-text-3)', fontSize: '.78rem' }}>
                    Refreshing onboarding...
                  </span>
                )}
              </div>
            )}

            {sitesPanelTab === 'onboarding' && isOnboardingManager ? (
              <ProjectOnboardingPage
                isAdmin={isAdmin}
                onOpenSiteViewer={onOpenSiteViewer}
                embeddedSiteId={selectedAsset.id}
                onSessionChange={handleOnboardingSessionChange}
                onProjectCreated={handleOnboardingSessionChange}
              />
            ) : (
              <>

            {/* Auto-assign on sync toggle - only show if config exists and user can manage */}
            {canManageAsset(selectedAsset) && assignmentConfig && (
              <div className="dt-card">
                <div className="c" style={{ padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    cursor: 'pointer',
                    fontSize: '.86rem',
                    fontWeight: 600,
                    color: 'var(--dt-text)',
                    userSelect: 'none'
                  }}>
                    <div
                      onClick={() => {
                        const newValue = !assignmentConfig.auto_assign_on_sync;
                        apiService.put(`/assets/${selectedAsset.id}/assignment-config/toggle`, { enabled: newValue })
                          .then(() => {
                            setAssignmentConfig((prev: any) => prev ? { ...prev, auto_assign_on_sync: newValue } : prev);
                          })
                          .catch(err => console.error('Failed to toggle auto-assign:', err));
                      }}
                      style={{
                        width: '36px',
                        height: '20px',
                        borderRadius: '10px',
                        background: assignmentConfig.auto_assign_on_sync ? 'var(--dt-accent)' : 'var(--dt-border-strong)',
                        position: 'relative',
                        cursor: 'pointer',
                        transition: 'background 0.2s',
                        flexShrink: 0
                      }}
                    >
                      <div style={{
                        width: '16px',
                        height: '16px',
                        borderRadius: '50%',
                        background: 'white',
                        position: 'absolute',
                        top: '2px',
                        insetInlineStart: assignmentConfig.auto_assign_on_sync ? '18px' : '2px',
                        transition: 'inset-inline-start 0.2s',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                      }} />
                    </div>
                    Auto-reassign on sync
                  </label>
                  <span style={{
                    fontSize: '.72rem',
                    color: 'var(--dt-text-3)',
                    marginInlineStart: 'auto'
                  }}>
                    {assignmentConfig.location_method && assignmentConfig.system_property
                      ? 'Location + Systems'
                      : assignmentConfig.location_method
                      ? 'Location only'
                      : assignmentConfig.system_property
                      ? 'Systems only'
                      : 'No config saved'}
                  </span>
                </div>
              </div>
            )}

            <div className="dt-seclabel" style={{ margin: '2px 0 0' }}>Files ({Object.keys(selectedAsset.files).length})</div>

            {/* File Type Sections */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* IFC Model Section */}
              <div className="dt-card">
                <div className="h">
                  <h3>3D Models (Revit, IFC, DWG){selectedAsset.files.ifc && selectedAsset.files.ifc.length > 0 ? ` (${selectedAsset.files.ifc.length})` : ''}</h3>
                  {/* RBAC: Show file management buttons if user can manage this asset */}
                  {canManageAsset(selectedAsset) && (
                    <div className="a" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={handleConnectACC}
                        className="dt-btn sm"
                        disabled={accLoading}
                        title={accConnections.length > 0 ? 'Browse ACC files or add another account' : 'Connect to Autodesk Construction Cloud'}
                      >
                        {accLoading ? 'Connecting...' : accConnections.length > 0 ? 'Browse ACC' : 'Connect ACC'}
                      </button>
                      <button
                        onClick={() => setShowIFCUploadModal(true)}
                        className="dt-btn sm pri"
                      >
                        Upload Model
                      </button>
                    </div>
                  )}
                </div>
                <div className="c">
                {selectedAsset.files.ifc && selectedAsset.files.ifc.length > 0 ? (
                  <div className="ifc-file-list">
                    {selectedAsset.files.ifc.map((ifcFile, index) => {
                      // Find matching ACC link for this IFC file (by comparing original filename)
                      const matchingAccLink = accLinks.find(link => {
                        const linkBaseName = link.acc_file_name.replace(/\.rvt$/i, '');
                        const fileBaseName = ifcFile.fileName.replace(/\.ifc$/i, '');
                        return fileBaseName.includes(linkBaseName) || linkBaseName.includes(fileBaseName.replace(/^file_[a-z0-9]+_/, ''));
                      });
                      const accStatus = matchingAccLink ? accService.formatSyncStatus(matchingAccLink) : null;
                      const matchingAccLevels = matchingAccLink ? accService.getLevels(matchingAccLink) : [];

                      return (
                        <div key={ifcFile.id} className="dt-frow" style={{ alignItems: 'flex-start' }}>
                          <span className="dt-fbadge">IFC</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dt-fname" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {ifcFile.fileName}
                              {ifcFile.isDefault && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-accent-strong)', background: 'var(--dt-accent-tint)' }}>{t('fileBadgeDefault', 'Default')}</span>
                              )}
                              {matchingAccLink && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-info)', background: 'var(--dt-info-t)' }}>
                                  ACC {accStatus?.label}
                                </span>
                              )}
                            </div>

                          <div className="ifc-file-meta">
                            {renderMetaItem('Size', `${(ifcFile.fileSize / 1024 / 1024).toFixed(2)} MB`)}
                            {matchingAccLink ? (
                              <>
                                {renderMetaItem('Source', <span className="ifc-meta-acc">{matchingAccLink.acc_project_name}</span>)}
                                {accService.isViewerReady(matchingAccLink) && renderSpawnLevelControl(
                                  matchingAccLink.default_level || '',
                                  matchingAccLevels,
                                  async (val) => {
                                    try {
                                      await accService.updateDefaultLevel(matchingAccLink.id, val || null);
                                      loadACCLinks(selectedAsset!.id);
                                    } catch (err) { console.error('Failed to update default level:', err); }
                                  },
                                  `acc:${matchingAccLink.id}`,
                                  () => refreshAccRvtLevels(matchingAccLink.id),
                                  canManageAsset(selectedAsset)
                                )}
                                {renderMetaItem('Last Sync', accService.formatLastSync(matchingAccLink))}
                              </>
                            ) : (
                              renderMetaItem('Uploaded', new Date(ifcFile.uploadedAt).toLocaleDateString())
                            )}
                          </div>

                          {ifcConversionStatus[ifcFile.id] && (
                            <div className="ifc-conversion-status">
                              <span className={`ifc-conversion-text ifc-conversion-${ifcConversionStatus[ifcFile.id].status}`}>
                                {ifcConversionStatus[ifcFile.id].status === 'converting' && 'Converting...'}
                                {ifcConversionStatus[ifcFile.id].status === 'completed' && 'Conversion Complete'}
                                {ifcConversionStatus[ifcFile.id].status === 'error' && 'Conversion Failed'}
                              </span>
                              {ifcConversionStatus[ifcFile.id].status === 'converting' && (
                                renderProgressBar(ifcConversionStatus[ifcFile.id].progress)
                              )}
                            </div>
                          )}

                          {canManageAsset(selectedAsset) && (
                            <div className="ifc-file-actions">
                              {renderActionBtn('settings', 'Properties', () => handleConfigurePropertySets(), { title: 'Configure property sets' })}
                              {selectedAsset.files.ifc && selectedAsset.files.ifc.length > 1 && !ifcFile.isDefault && (
                                renderActionBtn('star', 'Set Default', () => handleSetDefaultIFC(ifcFile), { title: 'Set as default' })
                              )}
                              {matchingAccLink && (
                                <>
                                  {renderActionBtn('sync', 'Sync', () => accService.triggerSync(matchingAccLink.id).then(() => { loadACCLinks(selectedAsset.id); onAccLinksChanged?.(); }).catch(err => showErrorToast(`Sync failed: ${err.message || err}`)), { disabled: matchingAccLink.sync_status === 'syncing', className: 'ifc-action-sync', title: 'Re-sync from ACC (downloads and translates file)' })}
                                  {renderActionBtn('play', 'Direct', () => handleUseDirectView(matchingAccLink.id), { disabled: !matchingAccLink.selected_version_urn || matchingAccLink.sync_status === 'syncing', className: 'pri', title: 'Use ACC derivatives directly (instant, no download)' })}
                                </>
                              )}
                              {renderActionBtn('delete', '', () => {
                                if (matchingAccLink) {
                                  if (confirm(`Delete IFC file and remove ACC link?`)) {
                                    accService.deleteLink(matchingAccLink.id)
                                      .then(() => handleFileDelete(selectedAsset.id, ifcFile.id, 'IFC'))
                                      .catch(err => console.error('Failed to delete ACC link:', err));
                                  }
                                } else {
                                  handleFileDelete(selectedAsset.id, ifcFile.id, 'IFC');
                                }
                              }, { className: 'ifc-action-delete', title: matchingAccLink ? 'Delete IFC and remove ACC link' : 'Delete file' })}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : accLinks.length > 0 ? (
                  // Show ACC links (syncing and synced SVF2)
                  <div className="ifc-file-list">
                    {/* SVF2 Synced RVT Files - Ready for Autodesk Viewer */}
                    {accLinks.filter(link => accService.isViewerReady(link)).map(link => {
                      const viewables = accService.getViewables(link);
                      const levels = accService.getLevels(link);
                      const views3d = viewables.filter(v => v.role === '3d');
                      return (
                        <div key={link.id} className="dt-frow" style={{ alignItems: 'flex-start' }}>
                          <span className="dt-fbadge" style={{ color: 'var(--dt-good)' }}>RVT</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dt-fname" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {link.acc_file_name}
                              <span className="dt-pill2" style={{ color: 'var(--dt-good)', background: 'var(--dt-good-t)' }}>
                                RVT (Autodesk Viewer)
                              </span>
                              {link.synced_version_number && (
                                <span className="dt-pill2">
                                  v{link.synced_version_number}
                                </span>
                              )}
                              {link.use_latest_version === false ? (
                                <span className="dt-pill2" style={{ color: 'var(--dt-warn)', background: 'var(--dt-warn-t)' }} title={t('fileBadgeLockedTitle', 'Auto-sync disabled - locked to specific version')}>{t('fileBadgeLocked', 'Locked')}</span>
                              ) : link.is_paused ? (
                                <span className="dt-pill2" style={{ color: 'var(--dt-warn)', background: 'var(--dt-warn-t)' }} title={t('fileBadgePausedTitle', 'Auto-sync is paused')}>{t('fileBadgePaused', 'Paused')}</span>
                              ) : (
                                <span className="dt-pill2" style={{ color: 'var(--dt-good)', background: 'var(--dt-good-t)' }} title="Auto-sync enabled - will update when file changes in ACC">Auto-sync</span>
                              )}
                            </div>
                          <div className="ifc-file-meta">
                            {renderMetaItem('Source', <span className="ifc-meta-acc">{link.acc_project_name}</span>)}
                            {renderMetaItem('Version', link.use_latest_version !== false
                              ? `Latest${link.synced_version_number ? ` (v${link.synced_version_number})` : ''}`
                              : `v${link.selected_version_number || link.synced_version_number || '?'}`
                            )}
                            {renderMetaItem('Views', `${views3d.length} 3D, ${viewables.filter(v => v.role === '2d').length} 2D`)}
                            {views3d.length > 1 && renderMetaSelect(
                              '3D View', link.selected_viewable_guid || '',
                              views3d.map(v => ({ key: v.guid, label: v.name })),
                              (val) => updateSelectedPhase(link.id, val),
                              'Select which 3D view/phase to open in viewer (shared for all users)',
                              'Latest (default)'
                            )}
                            {views3d.length > 0 && renderMetaSelect(
                              'Room Source', link.room_source_viewable_guid || '',
                              views3d.map(v => ({ key: v.guid, label: v.name })),
                              (val) => {
                                const guid = val || null;
                                const view = guid ? viewables.find(v => v.guid === guid) : null;
                                updateRoomSourcePhase(link.id, guid, view?.name || null);
                              },
                              'Select which phase to load room geometry from in the viewer',
                              'None'
                            )}
                            {renderSpawnLevelControl(
                              link.default_level || '',
                              levels,
                              async (val) => {
                                try {
                                  await accService.updateDefaultLevel(link.id, val || null);
                                  loadACCLinks(selectedAsset!.id);
                                } catch (err) { console.error('Failed to update default level:', err); }
                              },
                              `acc:${link.id}`,
                              () => refreshAccRvtLevels(link.id),
                              canManageAsset(selectedAsset)
                            )}
                            {renderMetaItem('Last Sync', accService.formatLastSync(link))}
                          </div>
                          <div className="ifc-file-actions">
                            {canManageAsset(selectedAsset) && (
                              <>
                                {renderActionBtn(link.is_paused ? 'play' : 'pause', link.is_paused ? 'Resume' : 'Pause', async () => {
                                  if (link.is_paused) { await accService.resumeSync(link.id); } else { await accService.pauseSync(link.id); }
                                  loadACCLinks(selectedAsset.id);
                                }, { className: link.is_paused ? 'ifc-action-resume' : 'ifc-action-pause', title: link.is_paused ? 'Resume auto-sync' : 'Pause auto-sync' })}
                                {renderActionBtn('edit', 'Version', () => handleChangeVersion(link), { title: 'Change synced version' })}
                                {renderActionBtn('sync', 'Sync', () => accService.triggerSync(link.id).then(() => { loadACCLinks(selectedAsset.id); onAccLinksChanged?.(); }).catch(err => showErrorToast(`Sync failed: ${err.message || err}`)), { disabled: link.sync_status === 'syncing', className: 'ifc-action-sync', title: 'Re-sync from ACC (downloads and translates file)' })}
                                {renderActionBtn('play', 'Direct', () => handleUseDirectView(link.id), { disabled: !link.selected_version_urn || link.sync_status === 'syncing', className: 'pri', title: 'Use ACC derivatives directly (instant, no download)' })}
                                {renderActionBtn('delete', 'Delete', () => {
                                  if (confirm(`Delete link to "${link.acc_file_name}"?`)) {
                                    accService.deleteLink(link.id).then(() => loadACCLinks(selectedAsset.id));
                                  }
                                }, { className: 'ifc-action-delete', title: 'Remove link' })}
                              </>
                            )}
                          </div>
                          </div>
                        </div>
                      );
                    })}
                    {/* ACC links that are still syncing (no IFC file yet) */}
                    {accLinks.filter(link => link.sync_status !== 'synced').map(link => {
                      const status = accService.formatSyncStatus(link);
                      return (
                        <div key={link.id} className="dt-frow" style={{ alignItems: 'flex-start' }}>
                          <span className="dt-fbadge">ACC</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dt-fname" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {link.acc_file_name}
                              <span className="dt-pill2" style={{ color: 'var(--dt-info)', background: 'var(--dt-info-t)' }}>
                                ACC {status.label}
                              </span>
                            </div>

                          <div className="ifc-file-meta">
                            {renderMetaItem('Source', <span className="ifc-meta-acc">{link.acc_project_name}</span>)}
                            {renderMetaItem('Version', link.use_latest_version !== false
                              ? 'Latest (auto-sync)'
                              : `v${link.selected_version_number || '?'} (locked)`
                            )}
                            {renderMetaItem('Status', <>
                              {link.sync_status === 'pending' && 'Waiting to start...'}
                              {link.sync_status === 'syncing' && (
                                accService.isSyncStuck(link)
                                  ? <span style={{ color: '#f59e0b' }}>Stuck at {link.sync_progress || 0}% - Click "Restart" below</span>
                                  : (link.conversion_type === 'svf2'
                                      ? `Preparing for viewer... ${link.sync_progress || 0}%`
                                      : `Converting to IFC... ${link.sync_progress || 0}%`)
                              )}
                              {link.sync_status === 'error' && (link.sync_error || 'Sync failed')}
                            </>)}
                          </div>

                          {link.sync_status === 'syncing' && renderProgressBar(
                            link.sync_progress || 0,
                            accService.isSyncStuck(link) ? `No progress for ${accService.getMinutesSinceUpdate(link)} minutes. Server may have restarted.` : undefined
                          )}

                          {canManageAsset(selectedAsset) && (
                            <div className="ifc-file-actions">
                              {accService.isSyncStuck(link) && renderActionBtn('restart', 'Restart', () => {
                                accService.triggerSync(link.id).then(() => { loadACCLinks(selectedAsset.id); onAccLinksChanged?.(); }).catch(err => showErrorToast(`Sync failed: ${err.message || err}`));
                              }, { className: 'ifc-action-sync', title: 'Restart stuck sync', style: { backgroundColor: '#f59e0b', borderColor: '#f59e0b' } })}
                              {renderActionBtn('play', 'Direct', () => handleUseDirectView(link.id), { disabled: !link.selected_version_urn, className: 'pri', title: 'Use ACC derivatives directly (instant, no download)' })}
                              {renderActionBtn('x', 'Cancel', async () => {
                                if (confirm(`Cancel sync for "${link.acc_file_name}"?`)) {
                                  await accService.cancelSync(link.id);
                                  loadACCLinks(selectedAsset.id);
                                }
                              }, { className: 'ifc-action-delete', title: 'Cancel sync' })}
                              {renderActionBtn('delete', 'Delete', () => {
                                if (confirm(`Delete link to "${link.acc_file_name}"?`)) {
                                  accService.deleteLink(link.id).then(() => loadACCLinks(selectedAsset.id));
                                }
                              }, { className: 'ifc-action-delete', title: 'Remove link' })}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                    })}
                    {accLinks.every(link => link.sync_status === 'synced') && (
                      <div className="dt-statebox">{t('noIfcModelsUploaded', 'No IFC models uploaded')}</div>
                    )}
                  </div>
                ) : getLocalRvtFiles(selectedAsset).length === 0 ? (
                  <div className="dt-statebox">{t('noModelsUploaded', 'No models uploaded')}</div>
                ) : null}

                {/* Local RVT file cards */}
                {getLocalRvtFiles(selectedAsset).length > 0 && (
                  <div className="ifc-file-list">
                    {getLocalRvtFiles(selectedAsset).map((rvtFile) => {
                      const rvtStatus = rvtFile.metadata?.rvt_translation_status;
                      const rvtPollStatus = rvtTranslationStatus[rvtFile.id];
                      const effectiveStatus = rvtPollStatus?.status || rvtStatus;
                      const rvtViewables = rvtFile.metadata?.viewables || [];
                      const rvtLevels = rvtFile.metadata?.levels || [];
                      const rvtViews3d = rvtViewables.filter((v: any) => v.role === '3d');
                      const rvtProgress = rvtPollStatus?.progress ?? rvtFile.metadata?.rvt_translation_progress ?? 0;

                      return (
                        <div key={rvtFile.id} className="dt-frow" style={{ alignItems: 'flex-start' }}>
                          <span className="dt-fbadge" style={{ color: effectiveStatus === 'ready' ? 'var(--dt-good)' : effectiveStatus === 'failed' ? 'var(--dt-bad)' : 'var(--dt-warn)' }}>RVT</span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div className="dt-fname" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                              {rvtFile.fileName}
                              <span className="dt-pill2" style={{ color: 'var(--dt-good)', background: 'var(--dt-good-t)' }}>
                                RVT (Local Upload)
                              </span>
                              {rvtFile.isDefault && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-accent-strong)', background: 'var(--dt-accent-tint)' }}>
                                  Default
                                </span>
                              )}
                              {effectiveStatus === 'translating' && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-warn)', background: 'var(--dt-warn-t)' }}>
                                  Translating... {rvtProgress > 0 ? `${rvtProgress}%` : ''}
                                </span>
                              )}
                              {effectiveStatus === 'ready' && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-good)', background: 'var(--dt-good-t)' }}>
                                  Ready
                                </span>
                              )}
                              {effectiveStatus === 'failed' && (
                                <span className="dt-pill2" style={{ color: 'var(--dt-bad)', background: 'var(--dt-bad-t)' }}>
                                  Translation Failed
                                </span>
                              )}
                            </div>
                          <div className="ifc-file-meta">
                            {renderMetaItem('Size', `${(rvtFile.fileSize / 1024 / 1024).toFixed(2)} MB`)}
                            {renderMetaItem('Uploaded', new Date(rvtFile.uploadedAt).toLocaleDateString())}
                            {effectiveStatus === 'ready' && rvtViewables.length > 0 && (
                              renderMetaItem('Views', `${rvtViews3d.length} 3D, ${rvtViewables.filter((v: any) => v.role === '2d').length} 2D`)
                            )}
                            {effectiveStatus === 'ready' && rvtViews3d.length > 1 && renderMetaSelect(
                              '3D View', rvtFile.metadata?.rvt_selected_viewable_guid || '',
                              rvtViews3d.map((v: any) => ({ key: v.guid, label: v.name })),
                              (val) => updateLocalRvtSetting(rvtFile.id, { selected_viewable_guid: val || null }),
                              'Select which 3D view/phase to open in viewer',
                              'Latest (default)'
                            )}
                            {effectiveStatus === 'ready' && renderSpawnLevelControl(
                              rvtFile.metadata?.rvt_default_level || '',
                              rvtLevels,
                              (val) => updateLocalRvtSetting(rvtFile.id, { default_level: val || null }),
                              `local:${rvtFile.id}`,
                              () => refreshLocalRvtLevels(rvtFile.id),
                              canManageAsset(selectedAsset)
                            )}
                          </div>

                          {effectiveStatus === 'translating' && (
                            <>
                              <div className="ifc-conversion-status">
                                <span className="ifc-conversion-text ifc-conversion-converting">
                                  Converting to Autodesk Viewer format...
                                </span>
                              </div>
                              {renderProgressBar(rvtProgress)}
                            </>
                          )}
                          {effectiveStatus === 'failed' && rvtFile.metadata?.rvt_translation_error && (
                            <div className="ifc-conversion-status">
                              <span className="ifc-conversion-text ifc-conversion-error">
                                {rvtFile.metadata.rvt_translation_error}
                              </span>
                            </div>
                          )}

                          {canManageAsset(selectedAsset) && (
                            <div className="ifc-file-actions">
                              {effectiveStatus === 'ready' && rvtFile.metadata?.viewable_urn && (
                                renderActionBtn('play', 'Open', () => handleOpenLocalRvt(rvtFile), { className: 'pri', title: 'Open in Autodesk Viewer' })
                              )}
                              {renderActionBtn('delete', '', () => handleFileDelete(selectedAsset.id, rvtFile.id, 'rvt'), { className: 'ifc-action-delete', title: 'Delete file' })}
                            </div>
                          )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                </div>
              </div>


              {/* E57 Panorama Section */}
              <div className="dt-card">
                <div className="h">
                  <h3>E57 Panorama Sources</h3>
                  {selectedAsset.permission_level === 'write' && (
                    <div className="a" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => setShowE57UploadModal(true)}
                        className="dt-btn sm pri"
                      >
                        Upload E57 Source
                      </button>
                      {selectedAssetE57Sources.length > 0 && (
                        <>
                          <button
                            onClick={handleShowPanoramaList}
                            className="dt-btn sm"
                            title="View panorama list"
                          >
                            View List
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="c">
                {selectedAssetE57Sources.length > 0 ? (
                  <div className="e57-source-list">
                    <div className="panorama-summary" style={{ fontSize: '.82rem', color: 'var(--dt-text-2)', marginBottom: '4px' }}>
                      <p>
                        <strong>{selectedAssetE57Sources.length}</strong> E57 source{selectedAssetE57Sources.length === 1 ? '' : 's'} ·{' '}
                        <strong>{selectedAssetE57PanoramaCount}</strong> panoramas extracted
                      </p>
                    </div>
                    {selectedAssetE57Sources.map((source, sourceIndex) => {
                      const coordinateReferenceDisplay = getE57CoordinateReferenceDisplay(selectedAsset, source);
                      const sourceCustomData = getE57SourceCustomData(selectedAsset, source);
                      const sourceCoordinateReference = (
                        sourceCustomData.coordinateReference ||
                        source.metadata?.coordinateReference ||
                        null
                      ) as E57CoordinateReferenceInfo | null;
                      const sphereNavigationSettings = getE57SphereNavigationSettings(selectedAsset, source);
                      const currentEpsgCode = sourceCoordinateReference?.authority === 'EPSG' && sourceCoordinateReference.code
                        ? String(sourceCoordinateReference.code)
                        : '';

                      return (
                        <div className="dt-frow" style={{ alignItems: 'flex-start' }} key={source.id}>
                          <span className="dt-fbadge">E57</span>
                          <div className="file-info e57-source-file-info" style={{ flex: 1, minWidth: 0 }}>
                            <div className="dt-fname">{source.fileName}</div>
                            <div className="dt-fmeta">
                              Source {sourceIndex + 1} · {(source.fileSize / 1024 / 1024).toFixed(2)} MB
                              {source.uploadedAt ? ` · ${new Date(source.uploadedAt).toLocaleString()}` : ''}
                            </div>
                            <div className="dt-fmeta panorama-count">
                              {source.panoramaCount || 0} panoramas extracted
                              {/* While their viewer copies are made (audit bug 76); gone once all are ready. */}
                              {(source.preparingCount || 0) > 0 && (
                                <> · {Math.max(0, (source.panoramaCount || 0) - (source.preparingCount || 0))} of {source.panoramaCount || 0} ready to view</>
                              )}
                            </div>
                            <div className="dt-fmeta e57-coordinate-reference" title={coordinateReferenceDisplay.detail || coordinateReferenceDisplay.label}>
                              {coordinateReferenceDisplay.label}
                              {coordinateReferenceDisplay.detail ? ` · ${coordinateReferenceDisplay.detail}` : ''}
                            </div>
                            {canManageAsset(selectedAsset) && (
                              <details className="e57-source-settings">
                                <summary className="e57-source-settings-summary">
                                  Coordinate and sphere navigation settings
                                </summary>
                                <div className="e57-source-settings-fields">
                                  <label className="e57-source-setting-label">
                                    <span>Manual EPSG</span>
                                    <input
                                      type="text"
                                      inputMode="numeric"
                                      defaultValue={currentEpsgCode}
                                      placeholder="2039"
                                      onBlur={async (e) => {
                                        await updateE57ManualCoordinateReference(source, e.target.value);
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Sphere placement</span>
                                    <select
                                      value={sphereNavigationSettings.placementMode}
                                      onChange={async (e) => {
                                        await updateE57SphereNavigationSettings(source, {
                                          placementMode: e.target.value === 'true_vector' ? 'true_vector' : 'legacy'
                                        });
                                      }}
                                      className="e57-source-setting-control"
                                    >
                                      <option value="legacy">Legacy</option>
                                      <option value="true_vector">True vector</option>
                                    </select>
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Scale to meters</span>
                                    <input
                                      type="number"
                                      min="0.000001"
                                      step="0.01"
                                      defaultValue={sphereNavigationSettings.scaleToMeters}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { scaleToMeters: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Vertical scale</span>
                                    <input
                                      type="number"
                                      min="0.000001"
                                      step="0.01"
                                      defaultValue={sphereNavigationSettings.verticalScaleToMeters}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { verticalScaleToMeters: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Camera height m</span>
                                    <input
                                      type="number"
                                      min="0.01"
                                      step="0.05"
                                      defaultValue={sphereNavigationSettings.cameraHeightAboveFloorMeters}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { cameraHeightAboveFloorMeters: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Max distance m</span>
                                    <input
                                      type="number"
                                      min="0.1"
                                      step="1"
                                      defaultValue={sphereNavigationSettings.maxDistanceMeters}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { maxDistanceMeters: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Near icon size</span>
                                    <input
                                      type="number"
                                      min="0.1"
                                      step="0.05"
                                      defaultValue={sphereNavigationSettings.markerNearSize}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { markerNearSize: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                  <label className="e57-source-setting-label">
                                    <span>Far icon size</span>
                                    <input
                                      type="number"
                                      min="0.1"
                                      step="0.05"
                                      defaultValue={sphereNavigationSettings.markerFarSize}
                                      onBlur={async (e) => {
                                        const value = Number(e.target.value);
                                        if (Number.isFinite(value) && value > 0) {
                                          await updateE57SphereNavigationSettings(source, { markerFarSize: value });
                                        }
                                      }}
                                      className="e57-source-setting-control"
                                    />
                                  </label>
                                </div>
                              </details>
                            )}
                          </div>
                          {canManageAsset(selectedAsset) && (
                            <button
                              onClick={() => handleFileDelete(selectedAsset.id, source.id, 'E57 source')}
                              className="dt-btn sm dt-dngr"
                              title="Delete E57 source"
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="dt-statebox">No E57 panorama sources uploaded</div>
                )}
                </div>
              </div>

              {selectedAssetE57Sources.length > 0 && selectedAssetHasRVTLink && (
                <div className="dt-card">
                  <div className="h">
                    <h3>Panorama to RVT Transform</h3>
                    {selectedAsset.permission_level === 'write' && panoramaToRVTTransform.enabled && (
                      <div className="a" style={{ display: 'flex', gap: '6px' }}>
                        <button
                          type="button"
                          onClick={resetPanoramaToRVTTransform}
                          className="dt-btn sm"
                          title="Remove explicit transform and use fallback alignment again"
                        >
                          Reset
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="c">
                  <div style={transformPanelStyle}>
                    <small style={{ lineHeight: 1.5, color: 'var(--text-secondary)' }}>
                      Native metadata is the preferred source for aligning E57 panorama poses to this site's RVT model.
                      Manual values are preserved as an advanced override.
                    </small>

                    {/* dir=ltr: source → target pipeline reads left-to-right regardless of UI language */}
                    <div dir="ltr" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                      <strong style={{ fontSize: '13px' }}>Status</strong>
                      <code>{panoramaAlignmentDiagnostics.status}</code>
                      <strong style={{ fontSize: '13px' }}>Frames</strong>
                      <code>{panoramaAlignmentDiagnostics.sourceFrame.id || 'panorama_raw'}</code>
                      <span>→</span>
                      <code>{panoramaAlignmentDiagnostics.targetFrame.id || 'rvt_model'}</code>
                      <strong style={{ fontSize: '13px' }}>Units</strong>
                      <code>{panoramaAlignmentDiagnostics.sourceFrame.unit || 'unknown'}</code>
                      <span>→</span>
                      <code>{panoramaAlignmentDiagnostics.targetFrame.unit || 'unknown'}</code>
                    </div>

                    <div style={transformFieldGridStyle}>
                      <div style={transformFieldStyle}>
                        <span>Transform source</span>
                        <code>{panoramaAlignmentDiagnostics.transformSource}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>Unit scale</span>
                        <code>{panoramaAlignmentDiagnostics.unitScale.x.toFixed(6)}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>Pose source</span>
                        <code>{panoramaAlignmentDiagnostics.sourceFrame.poseSource || panoramaAlignmentDiagnostics.sourceFrame.source || 'unknown'}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>E57 bounds</span>
                        <code>{formatCoordinateBounds(panoramaAlignmentDiagnostics.sourceFrame.bounds)}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>RVT bounds</span>
                        <code>{formatCoordinateBounds(panoramaAlignmentDiagnostics.targetFrame.bounds)}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>Global offset</span>
                        <code>{formatCoordinatePoint(panoramaAlignmentDiagnostics.viewerFrame.globalOffset || panoramaAlignmentDiagnostics.targetFrame.globalOffset)}</code>
                      </div>
                      <div style={transformFieldStyle}>
                        <span>Model to viewer</span>
                        <code>{panoramaAlignmentDiagnostics.viewerFrame.modelToViewerTransformAvailable === undefined ? 'unknown' : String(Boolean(panoramaAlignmentDiagnostics.viewerFrame.modelToViewerTransformAvailable))}</code>
                      </div>
                    </div>

                    {panoramaAlignmentDiagnostics.notes.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', color: 'var(--text-secondary)', fontSize: '12px', lineHeight: 1.4 }}>
                        {panoramaAlignmentDiagnostics.notes.map((note, index) => (
                          <span key={index}>{note}</span>
                        ))}
                      </div>
                    )}

                    <details style={{ width: '100%' }} open={panoramaAlignmentDiagnostics.hasLegacyOverride}>
                      <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}>
                        Advanced legacy manual override
                      </summary>
                      <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <small style={{ lineHeight: 1.5, color: 'var(--text-secondary)' }}>
                          Stored in <code>custom_data.coordinate_transforms.panorama_to_rvt_model</code> and used before native metadata when enabled.
                        </small>
                        {/* dir=ltr: source → target pipeline reads left-to-right regardless of UI language */}
                        <div dir="ltr" style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', fontSize: '13px' }}>
                          <strong style={{ fontSize: '13px' }}>Override frames</strong>
                          <code>{panoramaToRVTTransform.sourceFrame}</code>
                          <span>→</span>
                          <code>{panoramaToRVTTransform.targetFrame}</code>
                        </div>

                    {selectedAsset.permission_level === 'write' ? (
                      <>
                        <label style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', width: '100%' }}>
                          <input
                            type="checkbox"
                            checked={panoramaToRVTTransform.enabled}
                            onChange={async (e) => {
                              if (e.target.checked) {
                                await updatePanoramaToRVTTransform((currentTransform) => ({
                                  sourceFrame: 'panorama_raw',
                                  targetFrame: 'rvt_model',
                                  translation: {
                                    x: Number(currentTransform.translation?.x ?? 0),
                                    y: Number(currentTransform.translation?.y ?? 0),
                                    z: Number(currentTransform.translation?.z ?? 0)
                                  },
                                  rotationDeg: Number(currentTransform.rotationDeg ?? 0),
                                  scale: {
                                    x: Number(currentTransform.scale?.x ?? 1),
                                    y: Number(currentTransform.scale?.y ?? 1),
                                    z: Number(currentTransform.scale?.z ?? 1)
                                  },
                                  swapXY: Boolean(currentTransform.swapXY),
                                  flipX: Boolean(currentTransform.flipX),
                                  flipY: Boolean(currentTransform.flipY),
                                  flipZ: Boolean(currentTransform.flipZ)
                                }));
                              } else {
                                await resetPanoramaToRVTTransform();
                              }
                            }}
                            style={{ width: '16px', height: '16px', marginTop: '2px', flexShrink: 0 }}
                          />
                          <span style={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0 }}>
                            <strong style={{ fontSize: '13px' }}>Enable explicit transform</strong>
                            <small style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                              {panoramaToRVTTransform.enabled
                                ? 'Manual site calibration is active.'
                                : 'Fallback alignment heuristics are currently active.'}
                            </small>
                          </span>
                        </label>

                        {panoramaToRVTTransform.enabled && (
                          <>
                            <div style={transformFieldGridStyle}>
                              <label style={transformFieldStyle}>
                                <span>Offset X</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  defaultValue={panoramaToRVTTransform.translation.x}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value)) value = 0;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      translation: {
                                        ...(currentTransform.translation || {}),
                                        x: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>

                              <label style={transformFieldStyle}>
                                <span>Offset Y</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  defaultValue={panoramaToRVTTransform.translation.y}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value)) value = 0;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      translation: {
                                        ...(currentTransform.translation || {}),
                                        y: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>

                              <label style={transformFieldStyle}>
                                <span>Offset Z</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  defaultValue={panoramaToRVTTransform.translation.z}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value)) value = 0;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      translation: {
                                        ...(currentTransform.translation || {}),
                                        z: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>
                            </div>

                            <div style={transformFieldGridStyle}>
                              <label style={transformFieldStyle}>
                                <span>Rotation (deg)</span>
                                <input
                                  type="number"
                                  step="0.1"
                                  min="-360"
                                  max="360"
                                  defaultValue={panoramaToRVTTransform.rotationDeg}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value)) value = 0;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      rotationDeg: value
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>

                              <label style={transformFieldStyle}>
                                <span>Scale X</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  min="0.001"
                                  defaultValue={panoramaToRVTTransform.scale.x}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value) || value <= 0) value = 1;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      scale: {
                                        ...(currentTransform.scale || {}),
                                        x: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>

                              <label style={transformFieldStyle}>
                                <span>Scale Y</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  min="0.001"
                                  defaultValue={panoramaToRVTTransform.scale.y}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value) || value <= 0) value = 1;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      scale: {
                                        ...(currentTransform.scale || {}),
                                        y: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>

                              <label style={transformFieldStyle}>
                                <span>Scale Z</span>
                                <input
                                  type="number"
                                  step="0.001"
                                  min="0.001"
                                  defaultValue={panoramaToRVTTransform.scale.z}
                                  onBlur={async (e) => {
                                    let value = parseFloat(e.target.value);
                                    if (isNaN(value) || !isFinite(value) || value <= 0) value = 1;
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      scale: {
                                        ...(currentTransform.scale || {}),
                                        z: value
                                      }
                                    }));
                                  }}
                                  onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                  style={transformInputStyle}
                                />
                              </label>
                            </div>

                            <div style={transformToggleGridStyle}>
                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={panoramaToRVTTransform.swapXY}
                                  onChange={async (e) => {
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      swapXY: e.target.checked
                                    }));
                                  }}
                                />
                                <span>Swap X/Y</span>
                              </label>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={panoramaToRVTTransform.flipX}
                                  onChange={async (e) => {
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      flipX: e.target.checked
                                    }));
                                  }}
                                />
                                <span>Flip X</span>
                              </label>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={panoramaToRVTTransform.flipY}
                                  onChange={async (e) => {
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      flipY: e.target.checked
                                    }));
                                  }}
                                />
                                <span>Flip Y</span>
                              </label>

                              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                <input
                                  type="checkbox"
                                  checked={panoramaToRVTTransform.flipZ}
                                  onChange={async (e) => {
                                    await updatePanoramaToRVTTransform((currentTransform) => ({
                                      ...currentTransform,
                                      flipZ: e.target.checked
                                    }));
                                  }}
                                />
                                <span>Flip Z</span>
                              </label>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <small style={{ color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                        {panoramaToRVTTransform.enabled
                          ? 'Manual site calibration is active for Panorama to RVT.'
                          : 'This site is currently using fallback Panorama to RVT alignment.'}
                      </small>
                    )}
                      </div>
                    </details>
                  </div>
                  </div>
                </div>
              )}

              {/* GEOTIFF Section - Replacing DWG for 2D maps */}
              {/* Hide GEOTIFF section when RVT files exist (RVT has built-in 2D minimap) */}
              {!selectedAssetHasRVTLink && (
              <div className="dt-card">
                <div className="h">
                  <h3>2D Map (DWG, GEOTIFF)</h3>
                  {selectedAsset.permission_level === 'write' && (
                    <div className="a" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => setShowGEOTIFFUploadModal(true)}
                        className="dt-btn sm pri"
                      >
                        {selectedAsset.files.geotiff ? 'Replace GEOTIFF' : 'Upload GEOTIFF'}
                      </button>
                      {selectedAsset.files.geotiff && (
                        <button
                          onClick={() => handleFileDelete(selectedAsset.id, selectedAsset.files.geotiff.id, 'GEOTIFF')}
                          className="dt-btn sm dt-dngr"
                          title="Delete GEOTIFF"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  )}
                </div>
                <div className="c">
                {selectedAsset.files.geotiff ? (
                  <div className="file-item">
                    <div className="file-info">
                      <strong>{selectedAsset.files.geotiff.fileName}</strong>
                      <br />
                      <small>{(selectedAsset.files.geotiff.fileSize / 1024 / 1024).toFixed(2)} MB</small>
                    </div>

                    {/* Coordinate offsets and scaling - Only for write permission */}
                    {selectedAsset.permission_level === 'write' && (
                      <div style={{ marginTop: '10px', fontSize: '13px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {/* X Offset */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ minWidth: '90px' }}>X Offset:</span>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={selectedAsset.custom_data?.geotiff_x_offset ?? 0.0}
                            onBlur={async (e) => {
                              let offset = parseFloat(e.target.value);
                              if (isNaN(offset) || !isFinite(offset)) offset = 0.0;

                              await saveGeotiffSetting('geotiff_x_offset', offset, 'the X offset', e.target);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: '80px', padding: '4px' }}
                          />
                          <small style={{ color: '#888' }}>(default: 0.0)</small>
                        </label>

                        {/* Y Offset */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ minWidth: '90px' }}>Y Offset:</span>
                          <input
                            type="number"
                            step="0.1"
                            defaultValue={selectedAsset.custom_data?.geotiff_y_offset ?? 0.0}
                            onBlur={async (e) => {
                              let offset = parseFloat(e.target.value);
                              if (isNaN(offset) || !isFinite(offset)) offset = 0.0;

                              await saveGeotiffSetting('geotiff_y_offset', offset, 'the Y offset', e.target);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: '80px', padding: '4px' }}
                          />
                          <small style={{ color: '#888' }}>(default: 0.0)</small>
                        </label>

                        {/* Coordinate Scale */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ minWidth: '90px' }}>Scale:</span>
                          <input
                            type="number"
                            step="0.1"
                            min="0.001"
                            max="1000"
                            defaultValue={selectedAsset.custom_data?.geotiff_coordinate_scale ?? 1.0}
                            onBlur={async (e) => {
                              let scale = parseFloat(e.target.value);
                              // Validate and clamp
                              if (isNaN(scale) || !isFinite(scale)) scale = 1.0;
                              if (scale < 0.001) scale = 0.001;
                              if (scale > 1000) scale = 1000;

                              // Update asset with preserved custom_data
                              await saveGeotiffSetting('geotiff_coordinate_scale', scale, 'the coordinate scale', e.target);
                            }}
                            onKeyDown={(e) => {
                              // Save on Enter key
                              if (e.key === 'Enter') {
                                e.currentTarget.blur();
                              }
                            }}
                            style={{ width: '80px', padding: '4px' }}
                          />
                          <small style={{ color: '#888' }}>(default: 1.0)</small>
                        </label>

                        {/* Rotation Offset */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ minWidth: '90px' }}>Rotation (°):</span>
                          <input
                            type="number"
                            step="1"
                            min="-360"
                            max="360"
                            defaultValue={selectedAsset.custom_data?.geotiff_rotation_offset ?? 0.0}
                            onBlur={async (e) => {
                              let rotation = parseFloat(e.target.value);
                              if (isNaN(rotation) || !isFinite(rotation)) rotation = 0.0;
                              // Keep in -360 to 360 range
                              rotation = ((rotation % 360) + 360) % 360;

                              await saveGeotiffSetting('geotiff_rotation_offset', rotation, 'the rotation offset', e.target);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: '80px', padding: '4px' }}
                          />
                          <small style={{ color: '#888' }}>(default: 0.0)</small>
                        </label>

                        {/* Movement Scale - for world coordinate IFCs */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ minWidth: '90px' }}>Move Scale:</span>
                          <input
                            type="number"
                            step="1"
                            min="1"
                            max="1000"
                            defaultValue={selectedAsset.custom_data?.geotiff_movement_scale ?? 1000}
                            onBlur={async (e) => {
                              let scale = parseFloat(e.target.value);
                              if (isNaN(scale) || !isFinite(scale)) scale = 1000;
                              if (scale < 1) scale = 1;
                              if (scale > 1000) scale = 1000;

                              await saveGeotiffSetting('geotiff_movement_scale', scale, 'the movement scale', e.target);
                            }}
                            onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                            style={{ width: '80px', padding: '4px' }}
                          />
                          <small style={{ color: '#888' }}>(default: 1000)</small>
                        </label>

                        {/* World Coordinates Toggle */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          <input
                            type="checkbox"
                            checked={(() => {
                              const manualOverride = selectedAsset.custom_data?.geotiff_use_world_coordinates;
                              const refPoint = selectedAsset.custom_data?.ifc_shared_reference_point ?? { x: 0 };
                              // If manual override is set, use it; otherwise auto-detect
                              return manualOverride !== undefined
                                ? manualOverride
                                : Math.abs(refPoint.x) > 1000;
                            })()}
                            onChange={async (e) => {
                              await saveGeotiffSetting('geotiff_use_world_coordinates', e.target.checked, 'the world coordinates setting');
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span>World Coordinates</span>
                          <small style={{ color: '#888' }}>
                            {(() => {
                              const refPoint = selectedAsset.custom_data?.ifc_shared_reference_point ?? { x: 0 };
                              const autoDetected = Math.abs(refPoint.x) > 1000;
                              const manualOverride = selectedAsset.custom_data?.geotiff_use_world_coordinates;
                              if (manualOverride !== undefined) {
                                return `(manual: ${manualOverride ? 'ON' : 'OFF'})`;
                              }
                              return `(auto: ${autoDetected ? 'YES' : 'NO'})`;
                            })()}
                          </small>
                        </label>

                        {/* Use TFW World File Toggle - for direct coordinate mapping */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          <input
                            type="checkbox"
                            checked={selectedAsset.custom_data?.geotiff_use_tfw === true}
                            onChange={async (e) => {
                              await saveGeotiffSetting('geotiff_use_tfw', e.target.checked, 'the TFW setting');
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span>Use TFW</span>
                          <small style={{ color: '#888' }}>
                            (map model coords directly via .tfw)
                          </small>
                        </label>

                        {/* Disable Coordinate Sync Toggle - for testing world coordinate IFCs */}
                        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '4px' }}>
                          <input
                            type="checkbox"
                            checked={selectedAsset.custom_data?.geotiff_disable_coord_sync === true}
                            onChange={async (e) => {
                              await saveGeotiffSetting('geotiff_disable_coord_sync', e.target.checked, 'the coordinate sync setting');
                            }}
                            style={{ width: '16px', height: '16px' }}
                          />
                          <span>Disable Coord Sync</span>
                          <small style={{ color: '#888' }}>(use initial position only)</small>
                        </label>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="dt-statebox">No GEOTIFF map uploaded</div>
                )}
                </div>
              </div>
              )}

              {/* DWG Drawings Section - COMMENTED OUT - Using GEOTIFF instead
              <div className="file-type-section">
                <div className="file-type-header">
                  <h4>📐 2D Drawings (DWG)</h4>
                  <div className="file-actions">
                    <button
                      onClick={() => setShowDWGUploadModal(true)}
                      className="btn btn-primary btn-small"
                    >
                      {selectedAsset.files.dwg ? 'Replace DWG' : 'Upload DWG'}
                    </button>
                    {selectedAsset.files.dwg && (
                      <button
                        onClick={() => handleFileDelete(selectedAsset.id, selectedAsset.files.dwg.id, 'DWG')}
                        className="btn btn-danger btn-icon"
                        title="Delete DWG"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
                {selectedAsset.files.dwg ? (
                  <div className="file-item">
                    <div className="file-info">
                      <strong>{selectedAsset.files.dwg.fileName}</strong>
                      <br />
                      <small>{(selectedAsset.files.dwg.fileSize / 1024 / 1024).toFixed(2)} MB</small>
                    </div>
                  </div>
                ) : (
                  <div className="no-file">No DWG drawings uploaded</div>
                )}
              </div>
              */}

              {/* Assets (Points of Interest) Section */}
              <div className="dt-card">
                <div className="h">
                  <h3>Assets (CSV)</h3>
                  {selectedAsset.permission_level === 'write' && (
                    <div className="a" style={{ display: 'flex', gap: '6px' }}>
                      <button
                        onClick={() => setShowPOIUploadModal(true)}
                        className="dt-btn sm pri"
                      >
                        Upload Assets
                      </button>
                      {selectedAsset.files.poi && (
                        <>
                          <button
                            onClick={handleShowPOIList}
                            className="dt-btn sm"
                            title="View asset list"
                          >
                            View List
                          </button>
                          <button
                            onClick={() => handleFileDelete(selectedAsset.id, selectedAsset.files.poi.id, 'POI')}
                            className="dt-btn sm dt-dngr"
                            title="Delete assets"
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="c">
                {selectedAsset.files.poi ? (
                  <div className="dt-frow" style={{ alignItems: 'flex-start' }}>
                    <span className="dt-fbadge">CSV</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="dt-fname">{selectedAsset.files.poi.fileName}</div>
                      <div className="dt-fmeta">{selectedAsset.files.poi.poiCount || 0} assets uploaded</div>
                    </div>
                  </div>
                ) : (
                  <div className="dt-statebox">No asset files uploaded</div>
                )}
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        )}
        {!selectedAsset && (
          <div className="dt-card">
            <div className="c">
              <div className="dt-statebox">Select a site to manage its files</div>
            </div>
          </div>
        )}
      </div>

      {/* Guided / Create / Edit Site modals — extracted to src/pages/sites/SiteModals */}
      <SiteModals
        creatingGuidedSite={creatingGuidedSite}
        editAsset={editAsset}
        newAsset={newAsset}
        showCreateModal={showCreateModal}
        showEditModal={showEditModal}
        showGuidedCreateModal={showGuidedCreateModal}
        t={t}
        setEditAsset={setEditAsset}
        setNewAsset={setNewAsset}
        setShowCreateModal={setShowCreateModal}
        setShowEditModal={setShowEditModal}
        setShowGuidedCreateModal={setShowGuidedCreateModal}
        handleCreateAsset={handleCreateAsset}
        handleCreateGuidedSite={handleCreateGuidedSite}
        handleEditAsset={handleEditAsset}
      />

      {/* Upload / IFC-RVT / E57 / GEOTIFF / Asset upload modals — extracted to src/pages/sites/UploadModals */}
      <UploadModals
        analyzingIFC={analyzingIFC}
        getSelectedFile={getSelectedFile}
        isUploading={isUploading}
        selectedAsset={selectedAsset}
        showE57UploadModal={showE57UploadModal}
        showGEOTIFFUploadModal={showGEOTIFFUploadModal}
        showIFCUploadModal={showIFCUploadModal}
        showPOIUploadModal={showPOIUploadModal}
        showUploadModal={showUploadModal}
        t={t}
        uploadFileType={uploadFileType}
        uploadProgress={uploadProgress}
        setIfcUploadedFile={setIfcUploadedFile}
        setSelectedCompanionFiles={setSelectedCompanionFiles}
        setSelectedFile={setSelectedFile}
        setShowE57UploadModal={setShowE57UploadModal}
        setShowGEOTIFFUploadModal={setShowGEOTIFFUploadModal}
        setShowIFCUploadModal={setShowIFCUploadModal}
        setShowPOIUploadModal={setShowPOIUploadModal}
        setShowUploadModal={setShowUploadModal}
        setUploadFileType={setUploadFileType}
        handleFileUpload={handleFileUpload}
        handleSpecificFileUpload={handleSpecificFileUpload}
      />


      {/* Panorama List + Asset List modals — extracted to src/pages/sites/ListModals */}
      <ListModals
        canManageAsset={canManageAsset}
        isAdmin={isAdmin}
        deletingAllPOIs={deletingAllPOIs}
        deletingPOI={deletingPOI}
        editedPOIs={editedPOIs}
        isEditMode={isEditMode}
        loadingPOIs={loadingPOIs}
        loadingPanoramas={loadingPanoramas}
        panoramas={panoramas}
        pois={pois}
        savingPOIs={savingPOIs}
        selectedAsset={selectedAsset}
        selectedAssetE57Sources={selectedAssetE57Sources}
        selectedPOIs={selectedPOIs}
        showPOIListModal={showPOIListModal}
        showPanoramaListModal={showPanoramaListModal}
        setSelectedPOIs={setSelectedPOIs}
        setShowPOIListModal={setShowPOIListModal}
        setShowPanoramaListModal={setShowPanoramaListModal}
        handleCancelEditMode={handleCancelEditMode}
        handleDeleteAllPOIs={handleDeleteAllPOIs}
        handleDeletePOI={handleDeletePOI}
        handleEnterEditMode={handleEnterEditMode}
        handleOpenSetLocationsModal={handleOpenSetLocationsModal}
        handleOpenSetSystemsModal={handleOpenSetSystemsModal}
        handlePOIFieldChange={handlePOIFieldChange}
        handleSaveAllEdits={handleSaveAllEdits}
      />

      {/* IFC Category Selector + Property Set modals — extracted to src/pages/sites/ConfigModals */}
      <ConfigModals
        generatingPOIs={generatingPOIs}
        ifcAnalysisResult={ifcAnalysisResult}
        ifcUploadedFile={ifcUploadedFile}
        propertySetConfig={propertySetConfig}
        propertySetSearch={propertySetSearch}
        selectedAsset={selectedAsset}
        showIFCCategorySelector={showIFCCategorySelector}
        showPropertySetModal={showPropertySetModal}
        setPropertySetSearch={setPropertySetSearch}
        setShowPropertySetModal={setShowPropertySetModal}
        handleIFCCategoriesSelected={handleIFCCategoriesSelected}
        handleIFCCategoryCancelSelection={handleIFCCategoryCancelSelection}
        handleLocationsCreated={handleLocationsCreated}
        handleSavePropertySetConfig={handleSavePropertySetConfig}
        handleSelectAllPropertySets={handleSelectAllPropertySets}
        handleTogglePropertySet={handleTogglePropertySet}
        handleUnselectAllPropertySets={handleUnselectAllPropertySets}
      />

      {/* ACC Scope + Browser/Version modals — extracted to src/pages/sites/ACCModals */}
      <ACCModals
        accChangeVersionLink={accChangeVersionLink}
        accConnections={accConnections}
        accConversionType={accConversionType}
        accDisconnecting={accDisconnecting}
        accEnableMasterViews={accEnableMasterViews}
        accFileVersions={accFileVersions}
        accFolderContents={accFolderContents}
        accFolderPath={accFolderPath}
        accHubs={accHubs}
        accLinking={accLinking}
        accLoading={accLoading}
        accPendingLinkFile={accPendingLinkFile}
        accProjects={accProjects}
        accSelectedConnection={accSelectedConnection}
        accSelectedFileForLink={accSelectedFileForLink}
        accSelectedHub={accSelectedHub}
        accSelectedProject={accSelectedProject}
        accSelectedScope={accSelectedScope}
        accSelectedVersion={accSelectedVersion}
        accSelectedVersionUrn={accSelectedVersionUrn}
        accVersionsLoading={accVersionsLoading}
        accViewDirectMode={accViewDirectMode}
        showACCBrowserModal={showACCBrowserModal}
        showAccScopeModal={showAccScopeModal}
        showAccVersionModal={showAccVersionModal}
        setAccChangeVersionLink={setAccChangeVersionLink}
        setAccEnableMasterViews={setAccEnableMasterViews}
        setAccFolderContents={setAccFolderContents}
        setAccFolderPath={setAccFolderPath}
        setAccPendingLinkFile={setAccPendingLinkFile}
        setAccSelectedConnection={setAccSelectedConnection}
        setAccSelectedFileForLink={setAccSelectedFileForLink}
        setAccSelectedHub={setAccSelectedHub}
        setAccSelectedProject={setAccSelectedProject}
        setAccSelectedScope={setAccSelectedScope}
        setAccSelectedVersion={setAccSelectedVersion}
        setAccSelectedVersionUrn={setAccSelectedVersionUrn}
        setAccViewDirectMode={setAccViewDirectMode}
        setShowACCBrowserModal={setShowACCBrowserModal}
        setShowAccScopeModal={setShowAccScopeModal}
        setShowAccVersionModal={setShowAccVersionModal}
        handleAccScopeConfirm={handleAccScopeConfirm}
        handleChangeVersionSave={handleChangeVersionSave}
        handleVersionSelectAndViewDirect={handleVersionSelectAndViewDirect}
        handleVersionSelectAndLink={handleVersionSelectAndLink}
        handleACCSelectConnection={handleACCSelectConnection}
        handleDisconnectACC={handleDisconnectACC}
        handleACCAddNewConnection={handleACCAddNewConnection}
        handleACCSelectHub={handleACCSelectHub}
        handleACCSelectProject={handleACCSelectProject}
        handleACCNavigateUp={handleACCNavigateUp}
        handleACCNavigateFolder={handleACCNavigateFolder}
        handleACCViewDirect={handleACCViewDirect}
        handleACCLinkFile={handleACCLinkFile}
      />

      {/* Set Systems + Set Locations modals — extracted to src/pages/sites/BatchModals */}
      <BatchModals
        addSystemsSubcategoryLevel={addSystemsSubcategoryLevel}
        isApplyingLocations={isApplyingLocations}
        isApplyingSystems={isApplyingSystems}
        isLoadingLocationsProperties={isLoadingLocationsProperties}
        isLoadingSystemsProperties={isLoadingSystemsProperties}
        locationsAvailableProperties={locationsAvailableProperties}
        locationsResult={locationsResult}
        removeSystemsSubcategoryLevel={removeSystemsSubcategoryLevel}
        selectedPOIs={selectedPOIs}
        selectedSpaceProperty={selectedSpaceProperty}
        selectedStoreyProperty={selectedStoreyProperty}
        selectedSystemProperty={selectedSystemProperty}
        showSetLocationsModal={showSetLocationsModal}
        showSetSystemsModal={showSetSystemsModal}
        spacePropertySearch={spacePropertySearch}
        storeyPropertySearch={storeyPropertySearch}
        systemPropertySearch={systemPropertySearch}
        systemsAvailableProperties={systemsAvailableProperties}
        systemsPropertyValues={systemsPropertyValues}
        systemsResult={systemsResult}
        systemsSubcategoryLevels={systemsSubcategoryLevels}
        systemsSubcategorySearches={systemsSubcategorySearches}
        setLocationsResult={setLocationsResult}
        setSelectedSpaceProperty={setSelectedSpaceProperty}
        setSelectedStoreyProperty={setSelectedStoreyProperty}
        setSelectedSystemProperty={setSelectedSystemProperty}
        setShowSetLocationsModal={setShowSetLocationsModal}
        setShowSetSystemsModal={setShowSetSystemsModal}
        setSpacePropertySearch={setSpacePropertySearch}
        setStoreyPropertySearch={setStoreyPropertySearch}
        setSystemPropertySearch={setSystemPropertySearch}
        setSystemsResult={setSystemsResult}
        setSystemsSubcategoryLevels={setSystemsSubcategoryLevels}
        setSystemsSubcategorySearches={setSystemsSubcategorySearches}
        handleApplyLocationsBatch={handleApplyLocationsBatch}
        handleApplySystemsBatch={handleApplySystemsBatch}
      />

    </div>
  );
};

export default SitesPage;
